import './services/personal-workspace';
import './services/teejit-channels-view.js';
'use strict';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'material-icons/iconfont/material-icons.css';
import {createKhNotifier, khToastOptions} from './services/kh-notifications';
import {TeejitRestClient} from './services/teejit-rest-client.js';
import {refreshContentCertificateContext, setContentCompletionState} from './services/teejit-content-certificate.js';
import {TeejitSessionWarmup, updateTeejitProfileAvatarNodes} from './services/teejit-session-warmup.js';
import {getGrantedChannelJoinTeamId, channelProfileUrl} from './services/teejit-channel-join.js';
import {khRememberContentProgressUpdate} from './services/teejit-content-progress.js';
import {bootYoutubePreviews} from './services/teejit-youtube-preview.js';

window.khGetGrantedChannelJoinTeamId = getGrantedChannelJoinTeamId;

/* Load JavaScript Files  */
require('./init.js');
require('./contents/marked-content.js');
require('./contents/test.js');
require('./knowledgehub/knowledgehub-tour.js');

/* Load Theme Styles */
require('./../sass/index.scss');

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootYoutubePreviews, {once: true});
} else {
    bootYoutubePreviews();
}

const khLoadFeatureBundle = (loader, label) => {
    loader().catch((error) => {
        if (window.khThemeRuntime?.debugAjax) {
            console.error(label + ' bundle could not be loaded.', error);
        }
    });
};

const khLoadProjectsBundle = () => khLoadFeatureBundle(
    () => import(/* webpackChunkName: "projects" */ './contents/projects.js'),
    'Projects'
);

const khLoadFeatureBundleNearViewport = (element, loader, fallbackDelayMs = 0) => {
    if (!element) {
        return;
    }

    let loaded = false;
    const load = () => {
        if (loaded) {
            return;
        }
        loaded = true;
        loader();
    };

    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) {
                return;
            }

            observer.disconnect();
            load();
        }, {
            root: null,
            rootMargin: '0px 0px',
            threshold: 0,
        });
        observer.observe(element);
        return;
    }

    window.setTimeout(load, fallbackDelayMs);
};

const khBootDeferredFeatureBundles = () => {
    if (document.querySelector('#t-loginform, #t-registerform, .t-login')) {
        khLoadFeatureBundle(
            () => import(/* webpackChunkName: "login-device" */ './services/teejit-login-device.js'),
            'Login device'
        );
    }

    if (document.getElementById('kh-home-api-fragments-config') || document.getElementById('kh-home-builder')) {
        khLoadFeatureBundle(
            () => import(/* webpackChunkName: "home" */ './contents/home-hydrator.js'),
            'Home'
        );
    }

    if (document.getElementById('page-innohub-my-projects')) {
        khLoadProjectsBundle();
    } else if (document.getElementById('kh-home-projects')) {
        khLoadFeatureBundleNearViewport(
            document.getElementById('kh-home-project-list') || document.getElementById('kh-home-projects'),
            khLoadProjectsBundle,
            3500
        );
    }

    if (document.getElementById('page-innohub-methodenfinder')) {
        khLoadFeatureBundle(
            () => import(/* webpackChunkName: "methodenfinder" */ './contents/methodenfinder.js'),
            'Method finder'
        );
    }
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', khBootDeferredFeatureBundles, {once: true});
} else {
    khBootDeferredFeatureBundles();
}

const khRuntime = window.khThemeRuntime || {};
const khRestBaseUrl = String(khRuntime.restBaseUrl || '/wp-json/knowhub/v2').replace(/\/+$/, '');
const khUserAccessToken = String(khRuntime.userAccessToken || '');
const khMarkContentLabels = khRuntime.markContent || {};
const khMessages = khRuntime.messages || {};
const khActiveRestAjaxRequests = new Set();
const khUserRestClient = new TeejitRestClient({
    restBaseUrl: khRestBaseUrl,
    accessToken: khUserAccessToken,
    nonce: khRuntime.restNonce || '',
});

const khMessage = (key, fallback) => window.khThemeRuntime?.messages?.[key] || khMessages[key] || fallback;
const khMarkContentLabel = (key, fallback) => window.khThemeRuntime?.markContent?.[key] || khMarkContentLabels[key] || fallback;

const khRestUrl = (path) => khRestBaseUrl + '/' + String(path || '').replace(/^\/+/, '');

const khReadHomeHydratorConfig = () => {
    const configEl = document.getElementById('kh-home-api-fragments-config');
    if (!configEl) {
        return null;
    }

    try {
        return JSON.parse(configEl.textContent || '{}');
    } catch (error) {
        return {};
    }
};

const khIsHomepageBootstrapActive = () => {
    const config = khReadHomeHydratorConfig();
    return !!config && config.bootstrapEnabled !== false && !!config.bootstrapEndpoint;
};

const khGetHomeBootstrapPayload = () => window.khHomeBootstrapPayload || window.khHomeHydrator?.bootstrapPayload || null;

const khGetFrontendLocale = () => String(window.khThemeRuntime?.frontendLocale || khRuntime.frontendLocale || 'de_DE').trim();

const khAbortActiveRestAjaxRequests = () => {
    khActiveRestAjaxRequests.forEach((request) => {
        if (request && typeof request.abort === 'function') {
            request.abort();
        }
    });
    khActiveRestAjaxRequests.clear();
};

document.addEventListener('kh:frontendLocaleChanging', khAbortActiveRestAjaxRequests);

const khAuthHeaders = () => {
    const frontendLocale = khGetFrontendLocale();

    return {
        ...(khUserAccessToken ? { Authorization: 'Bearer ' + khUserAccessToken } : {}),
        ...(frontendLocale ? { 'X-Frontend-Locale': frontendLocale } : {}),
    };
};

const khLocalNotificationPrefix = 'local:';

const khLocalNotificationsStorageKey = () => 'khLocalNotifications:v1:' + String(khRuntime.userId || 'current');

const khLeftChannelsStorageKey = () => 'khLeftChannels:v1:' + String(khRuntime.userId || 'current');

const khNormalizeChannelId = (teamId) => String(teamId || '').trim();

const khRuntimeSessionKey = (prefix) => {
    const rawToken = String(khRuntime.userAccessToken || '');
    let tokenHash = 0;
    for (let index = 0; index < rawToken.length; index += 1) {
        tokenHash = ((tokenHash << 5) - tokenHash + rawToken.charCodeAt(index)) | 0;
    }

    return prefix + ':' + String(khRuntime.userId || 'current') + ':' + Math.abs(tokenHash);
};

const khReadLeftChannelIds = () => {
    try {
        const stored = window.localStorage.getItem(khLeftChannelsStorageKey());
        const parsed = stored ? JSON.parse(stored) : [];
        const now = Date.now();
        const validUntil = 7 * 24 * 60 * 60 * 1000;

        return (Array.isArray(parsed) ? parsed : [])
            .filter((entry) => entry && typeof entry === 'object')
            .filter((entry) => now - (parseInt(entry.leftAt, 10) || 0) < validUntil)
            .map((entry) => khNormalizeChannelId(entry.id))
            .filter(Boolean);
    } catch (error) {
        return [];
    }
};

const khWriteLeftChannelIds = (teamIds) => {
    try {
        const now = Date.now();
        const entries = Array.from(new Set((Array.isArray(teamIds) ? teamIds : [])
            .map(khNormalizeChannelId)
            .filter(Boolean)))
            .map((id) => ({id, leftAt: now}));
        window.localStorage.setItem(khLeftChannelsStorageKey(), JSON.stringify(entries.slice(0, 50)));
    } catch (error) {
    }
};

const khStoreLeftChannelId = (teamId) => {
    const id = khNormalizeChannelId(teamId);
    if (!id) {
        return;
    }

    khWriteLeftChannelIds([id].concat(khReadLeftChannelIds()));
};

const khClearLeftChannelId = (teamId) => {
    const id = khNormalizeChannelId(teamId);
    if (!id) {
        return;
    }

    khWriteLeftChannelIds(khReadLeftChannelIds().filter((storedId) => storedId !== id));
};

const khRemoveLeftChannelDom = (teamId) => {
    const id = khNormalizeChannelId(teamId);
    if (!id) {
        return;
    }

    document.querySelectorAll(
        '#lef-nav-' + id +
        ', #t-nav-left-team-list [data-channel-id="' + id + '"]' +
        ', #t-nav-left-team-list [data-team-id="' + id + '"]' +
        ', .t-nav-home-flag-clients [data-kh-home-team-flag-id="' + id + '"]'
    ).forEach((element) => {
        element.remove();
    });
};

const khApplyLeftChannelFilters = () => {
    khReadLeftChannelIds().forEach(khRemoveLeftChannelDom);
};

const khLeftNavIconsStorageKey = () => khRuntimeSessionKey('khLeftNavIcons:v1');

const khNormalizeMaterialNavIcon = (value, fallback = '') => {
    const icon = String(value || '')
        .trim()
        .replace(/^material-[ors]-/i, '')
        .replace(/-/g, '_')
        .replace(/[^a-z0-9_]/ig, '');

    return icon || fallback;
};

const khExtractTeamNavIcon = (team) => {
    if (!team || typeof team !== 'object') {
        return '';
    }

    const iconKeys = ['icon', 'team_image_icon', 'icon_shortcode', 'material_icon', 'icon_name'];
    for (const key of iconKeys) {
        let value = team[key];
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            value = value.icon || value.name || value.shortcode || value.value || '';
        }

        const icon = khNormalizeMaterialNavIcon(value);
        if (icon) {
            return icon;
        }
    }

    return '';
};

const khNormalizeLeftNavTeams = (source) => {
    const teams = Array.isArray(source) ? source : [];
    const seen = {};

    return teams.reduce((carry, team) => {
        if (!team || typeof team !== 'object') {
            return carry;
        }

        const id = parseInt(team.id || team.team_id || 0, 10);
        const icon = khExtractTeamNavIcon(team);
        if (!id || !icon || seen[id]) {
            return carry;
        }

        seen[id] = true;
        carry.push({id, icon});
        return carry;
    }, []);
};

const khReadLeftNavIconCache = () => {
    try {
        const stored = window.sessionStorage.getItem(khLeftNavIconsStorageKey());
        const parsed = stored ? JSON.parse(stored) : [];
        return khNormalizeLeftNavTeams(Array.isArray(parsed) ? parsed : []);
    } catch (error) {
        return [];
    }
};

const khWriteLeftNavIconCache = (teams) => {
    try {
        const normalized = khNormalizeLeftNavTeams(teams);
        if (normalized.length) {
            window.sessionStorage.setItem(khLeftNavIconsStorageKey(), JSON.stringify(normalized));
        }
    } catch (error) {
    }
};

const khHydrateLeftNavIcons = (payload) => {
    const list = document.getElementById('t-nav-left-team-list');
    if (!list) {
        return;
    }

    const teams = khNormalizeLeftNavTeams(payload?.teams || payload || []);
    if (!teams.length) {
        return;
    }

    khWriteLeftNavIconCache(teams);
    teams.forEach((team) => {
        const wrapper = document.getElementById('lef-nav-' + team.id);
        const icon = wrapper ? wrapper.querySelector('.t-nav-list-element-icon.material-symbols-outlined') : null;
        if (icon) {
            icon.textContent = khNormalizeMaterialNavIcon(team.icon, 'explore');
        }
    });
};

const khBootLeftNavIconHydration = () => {
    if (!document.getElementById('t-nav-left-team-list')) {
        return;
    }

    khHydrateLeftNavIcons(khReadLeftNavIconCache());

    if (!khUserAccessToken) {
        return;
    }

    new TeejitSessionWarmup({
        endpoint: khRuntime.sessionWarmupEndpoint,
        restClient: khUserRestClient,
    }).warmup()
        .then(khHydrateLeftNavIcons)
        .catch(() => null);
};

window.addEventListener('kh:sessionWarmupReady', (event) => {
    khHydrateLeftNavIcons(event.detail || {});
});

const khBootLeftChannelFilters = () => {
    if (!khReadLeftChannelIds().length) {
        khApplyLeftChannelFilters();
        return;
    }

    khRefreshLeftChannelCache()
        .catch(() => null)
        .finally(() => {
            khApplyLeftChannelFilters();
        });
};

const khReadLocalNotifications = () => {
    try {
        const stored = window.localStorage.getItem(khLocalNotificationsStorageKey());
        const parsed = stored ? JSON.parse(stored) : [];
        return Array.isArray(parsed) ? parsed.filter((notification) => notification && typeof notification === 'object') : [];
    } catch (error) {
        return [];
    }
};

const khWriteLocalNotifications = (notifications) => {
    try {
        const normalized = Array.isArray(notifications) ? notifications : [];
        window.localStorage.setItem(khLocalNotificationsStorageKey(), JSON.stringify(normalized.slice(0, 30)));
    } catch (error) {
    }
};

const khStoreLocalNotification = (notification) => {
    const createdAt = new Date().toISOString();
    const normalized = Object.assign({
        id: khLocalNotificationPrefix + Date.now(),
        icon: 'notifications',
        title: khMessage('notificationsTitleFallback', 'Notification'),
        message: '',
        url: '',
        read_at: null,
        created_at: createdAt,
    }, notification || {});

    normalized.id = String(normalized.id || khLocalNotificationPrefix + Date.now());
    if (normalized.id.indexOf(khLocalNotificationPrefix) !== 0) {
        normalized.id = khLocalNotificationPrefix + normalized.id;
    }

    const notifications = khReadLocalNotifications()
        .filter((item) => String(item.id || '') !== normalized.id);
    khWriteLocalNotifications([normalized].concat(notifications));

    document.dispatchEvent(new CustomEvent('kh:notifications-local-updated'));
};

const khMarkLocalNotificationRead = (notificationId) => {
    const targetId = String(notificationId || '');
    if (!targetId) {
        return;
    }

    khWriteLocalNotifications(khReadLocalNotifications().map((notification) => {
        if (String(notification.id || '') !== targetId) {
            return notification;
        }

        return Object.assign({}, notification, {
            read_at: notification.read_at || new Date().toISOString(),
        });
    }));
};

const khMarkAllLocalNotificationsRead = () => {
    const readAt = new Date().toISOString();
    khWriteLocalNotifications(khReadLocalNotifications().map((notification) => Object.assign({}, notification, {
        read_at: notification.read_at || readAt,
    })));
};

const khRenderLoadingSvg = (size = 50) => {
    const dimension = Math.max(1, parseInt(size, 10) || 50);

    return '<svg class="t-loading profile-save-loader" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 436.8 426.76" width="' + dimension + 'px" height="' + dimension + 'px" aria-hidden="true" focusable="false">' +
        '<path class="rotating-group" d="M195.22,393.82c-87.1-11.23-155.38-85-157.53-175.95-1.83-77.32,44.66-144.49,111.76-172.31l24.23-35.63C80.73,30.5,11.16,113.86,11.16,213.59s72.03,186,167.43,204.68l16.63-24.45Z"/>' +
        '<path class="rotating-group" d="M249.79,7.43l-17.01,25.01c91.26,7.23,164.1,82.87,166.33,176.86,1.94,82.07-50.56,152.7-124.36,176.99l-22.47,33.04c98.34-16.27,173.36-102.19,173.36-205.75S349.35,22.65,249.79,7.43Z"/>' +
        '<path class="rotating-group teejit-loading-2" d="M181.85,340.25c-55.23-15.86-95.64-66.74-95.64-127.07s41.57-112.65,98-127.72l23.06-26.17c-80.01,5.71-143.15,72.42-143.15,153.88,0,66.79,42.44,123.66,101.82,145.13l15.92-18.06Z"/>' +
        '<path class="rotating-group teejit-loading-2" d="M263.01,65.46l-16.37,18.57c59.44,12.94,103.95,65.84,103.95,129.16s-47.83,120.07-110.52,130.41l-21.03,23.87c84.92-.34,153.65-69.28,153.65-154.28,0-69.7-46.22-128.59-109.68-147.73Z"/>' +
        '</svg>';
};

const khRefreshSessionData = (delay = 0, success) => {
    return new Promise((resolve, reject) => {
        window.setTimeout(() => {
            TeejitRestClient.invalidateRuntimeCaches();

            khRestAjax({
                method: 'POST',
                path: 'user/profile/refresh',
                success: function (data) {
                    TeejitRestClient.invalidateRuntimeCaches();

                    if (typeof success === 'function') {
                        success(data);
                    }
                    resolve(data);
                },
                error: reject,
            });
        }, delay);
    });
};

const khInvalidateRuntimeDataCaches = (method) => {
    if (String(method || 'GET').toUpperCase() === 'GET') {
        return;
    }

    TeejitRestClient.invalidateRuntimeCaches();
};

window.khInvalidateRuntimeDataCaches = khInvalidateRuntimeDataCaches;

const khReloadAfterRefreshData = (delay = 0) => {
    khRefreshSessionData(delay)
        .catch(() => null)
        .finally(() => {
            window.location.reload();
        });
};

window.khRefreshSessionData = khRefreshSessionData;

const khReloadWithRefreshData = (delay = 0) => {
    khReloadAfterRefreshData(delay);
};

const khFinishChannelJoin = (redirectTeamId, reloadDelay = 0) => {
    const targetUrl = redirectTeamId
        ? channelProfileUrl(redirectTeamId, khRuntime.channelProfileUrl || new URL('/teejit/team/', window.location.origin).href)
        : null;

    return khRefreshSessionData(targetUrl ? 0 : reloadDelay)
        .catch(() => null)
        .finally(() => {
            if (targetUrl) {
                window.location.assign(targetUrl);
            } else {
                window.location.reload();
            }
        });
};

const khBootSessionWarmup = () => {
    if (khIsHomepageBootstrapActive()) {
        return;
    }

    TeejitSessionWarmup.bootFromRuntime();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', khBootSessionWarmup, {once: true});
    document.addEventListener('DOMContentLoaded', khBootLeftChannelFilters, {once: true});
    document.addEventListener('DOMContentLoaded', khBootLeftNavIconHydration, {once: true});
} else {
    khBootSessionWarmup();
    khBootLeftChannelFilters();
    khBootLeftNavIconHydration();
}

const khTrackContentAnalytics = (contentId, contentTypeId, data = {}, callbacks = {}) => {
    const normalizedContentId = parseInt(contentId, 10);
    const normalizedContentTypeId = parseInt(contentTypeId, 10);

    if (!Number.isFinite(normalizedContentId) || normalizedContentId <= 0 || !Number.isFinite(normalizedContentTypeId) || normalizedContentTypeId <= 0) {
        return null;
    }

    return khRestAjax({
        method: 'POST',
        path: 'user/analytics/content/' + encodeURIComponent(normalizedContentId) + '/' + encodeURIComponent(normalizedContentTypeId),
        data,
        success: function (responseData) {
            khRememberContentProgressUpdate(normalizedContentId, normalizedContentTypeId, data);
            if (typeof callbacks.success === 'function') {
                callbacks.success(responseData);
            }
        },
        error: callbacks.error,
        complete: callbacks.complete,
    });
};

const khExtractDownloadFilename = ($link, href, response) => {
    const contentDisposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition') || '';
    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch (error) {
            return utf8Match[1];
        }
    }

    const plainMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i) || contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    if (plainMatch && plainMatch[1]) {
        return plainMatch[1].trim();
    }

    const fileNameFromData = $link.data('file-name');
    if (typeof fileNameFromData === 'string' && fileNameFromData !== '') {
        const segments = fileNameFromData.split('/');
        return segments[segments.length - 1];
    }

    try {
        const url = new URL(href, window.location.origin);
        const fileName = url.searchParams.get('file_name');
        if (fileName) {
            const segments = fileName.split('/');
            return decodeURIComponent(segments[segments.length - 1]);
        }
    } catch (error) {
    }

    return 'attachment-download';
};

const khTriggerBlobDownload = (blob, filename) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    window.setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
    }, 0);
};

const khDownloadAttachment = async ($link) => {
    const href = $link.attr('href') || '';
    if (!href) {
        return;
    }

    const headers = {};
    if (khUserAccessToken) {
        headers.Authorization = 'Bearer ' + khUserAccessToken;
    }
    const frontendLocale = khGetFrontendLocale();
    if (frontendLocale) {
        headers['X-Frontend-Locale'] = frontendLocale;
    }

    const response = await window.fetch(href, {
        method: 'GET',
        credentials: 'same-origin',
        headers,
        cache: 'no-store',
    });

    if (!response.ok) {
        return;
    }

    const blob = await response.blob();
    const filename = khExtractDownloadFilename($link, href, response);
    khTriggerBlobDownload(blob, filename);
};

const khCanTrackWithConsent = () => {
    if (typeof cookieConsentData === 'undefined' || cookieConsentData === null) {
        return true;
    }

    if (typeof cookieConsentData.parsed === 'undefined' || cookieConsentData.parsed === null) {
        return true;
    }

    return cookieConsentData.parsed.preferences === true;
};

const khRestAjax = ({method = 'POST', path, data = {}, success, error, complete}) => {
    const payload = Object.assign({}, data);
    const normalizedMethod = String(method || 'POST').toUpperCase();
    const frontendLocale = khGetFrontendLocale();
    if (khUserAccessToken && !payload.user_access_token) {
        payload.user_access_token = khUserAccessToken;
    }
    if (frontendLocale && !payload.frontend_locale && !payload.locale) {
        payload.frontend_locale = frontendLocale;
    }

    const ajaxOptions = {
        type: normalizedMethod,
        url: khRestUrl(path),
        data: normalizedMethod === 'GET' ? payload : JSON.stringify(payload),
        dataType: 'json',
        headers: khAuthHeaders(),
        success: function (responseData, textStatus, jqXHR) {
            khInvalidateRuntimeDataCaches(normalizedMethod);

            if (typeof success === 'function') {
                success(responseData, textStatus, jqXHR);
            }
        },
        error,
        complete,
    };

    if (normalizedMethod !== 'GET') {
        ajaxOptions.contentType = 'application/json; charset=UTF-8';
    }

    const request = jQuery.ajax(ajaxOptions);
    khActiveRestAjaxRequests.add(request);
    request.always(() => {
        khActiveRestAjaxRequests.delete(request);
    });

    return request;
};

const khProfileTabActionFromUrl = (url) => {
    try {
        const parsed = new URL(url, window.location.origin);
        const action = parsed.searchParams.get('action') || 'personal-data';
        return action === 'connections' ? 'sessions' : action;
    } catch (error) {
        return 'personal-data';
    }
};

const khProfileTabUrl = (action) => {
    const url = new URL(window.location.href);
    url.searchParams.set('action', action || 'personal-data');
    url.searchParams.delete('edit');
    url.searchParams.delete('delete');
    url.searchParams.delete('update');
    url.hash = '';
    return url;
};

const khSetProfileTabActiveState = (action) => {
    document.querySelectorAll('[data-profile-tab-link]').forEach((link) => {
        const isActive = String(link.getAttribute('data-profile-action') || '') === String(action || '');
        const element = link.querySelector('.t-content-navi-element');
        link.toggleAttribute('aria-current', isActive);
        if (isActive) {
            link.setAttribute('aria-current', 'page');
        }
        if (element) {
            element.classList.toggle('active', isActive);
        }
    });
};

const khUpdateProfileTabLoaderPosition = (panel) => {
    if (!panel) {
        return;
    }

    const rect = panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const minViewportInset = 80;
    const visibleTop = Math.max(rect.top, minViewportInset);
    const visibleBottom = Math.min(rect.bottom, Math.max(minViewportInset, viewportHeight - minViewportInset));
    const visibleLeft = Math.max(rect.left, 0);
    const visibleRight = Math.min(rect.right, viewportWidth);
    const fallbackTop = Math.min(Math.max(rect.top + Math.min(rect.height / 2, 180), minViewportInset), Math.max(minViewportInset, viewportHeight - minViewportInset));
    const fallbackLeft = Math.min(Math.max(rect.left + (rect.width / 2), 0), viewportWidth);
    const loaderTop = visibleBottom > visibleTop ? visibleTop + ((visibleBottom - visibleTop) / 2) : fallbackTop;
    const loaderLeft = visibleRight > visibleLeft ? visibleLeft + ((visibleRight - visibleLeft) / 2) : fallbackLeft;

    panel.style.setProperty('--kh-profile-tab-loader-top', `${loaderTop}px`);
    panel.style.setProperty('--kh-profile-tab-loader-left', `${loaderLeft}px`);
};

const khSetProfileTabLoadingState = (panel, isLoading) => {
    if (!panel) {
        return;
    }

    if (isLoading) {
        khUpdateProfileTabLoaderPosition(panel);
    }

    panel.classList.toggle('is-loading', isLoading);
    panel.setAttribute('aria-busy', isLoading ? 'true' : 'false');
};

const khExecuteProfileTabScripts = (root) => {
    if (!root) {
        return;
    }

    root.querySelectorAll('script').forEach((script) => {
        const replacement = document.createElement('script');
        Array.from(script.attributes).forEach((attribute) => {
            replacement.setAttribute(attribute.name, attribute.value);
        });
        replacement.textContent = script.textContent || '';
        script.parentNode.replaceChild(replacement, script);
    });
};

const khBootProfileTabs = () => {
    const profilePage = document.getElementById('page-profile');
    if (!profilePage) {
        return;
    }

    const panel = profilePage.querySelector('[data-profile-tab-panel]');
    const content = profilePage.querySelector('[data-profile-tab-content]');
    if (!panel || !content) {
        return;
    }

    let activeRequest = null;
    let loaderPositionFrame = null;
    const updateLoaderPosition = () => {
        if (!panel.classList.contains('is-loading')) {
            return;
        }

        if (loaderPositionFrame) {
            window.cancelAnimationFrame(loaderPositionFrame);
        }

        loaderPositionFrame = window.requestAnimationFrame(() => {
            loaderPositionFrame = null;
            khUpdateProfileTabLoaderPosition(panel);
        });
    };

    window.addEventListener('scroll', updateLoaderPosition, {passive: true});
    window.addEventListener('resize', updateLoaderPosition);

    const notifier = createKhNotifier({
        labels: {
            info: khMessage('noticeProfile', 'Profile'),
            alert: khMessage('noticeProfile', 'Profile'),
        }
    });

    const loadTab = (action, options = {}) => {
        const requestedAction = String(action || 'personal-data');
        const normalizedAction = requestedAction === 'connections' ? 'sessions' : requestedAction;
        const targetUrl = khProfileTabUrl(normalizedAction);

        if (activeRequest && typeof activeRequest.abort === 'function') {
            activeRequest.abort();
        }

        khSetProfileTabLoadingState(panel, true);
        khSetProfileTabActiveState(normalizedAction);

        const request = khRestAjax({
            method: 'GET',
            path: 'theme/profile/tab',
            data: {
                action: normalizedAction,
            },
            success: function (data) {
                const payload = getApiPayload(data) || data || {};
                if (!payload.html) {
                    notifier.alert(khMessage('profileTabLoadError', 'The profile area could not be loaded.'));
                    return;
                }

                content.innerHTML = String(payload.html);
                khExecuteProfileTabScripts(content);
                khBootProfileSessions(content);
                khRefreshLeftChannelCache()
                    .catch(() => null)
                    .finally(() => {
                        khApplyLeftChannelFilters();
                    });
                if (payload.title) {
                    const separator = document.title.indexOf(' - ') !== -1 ? ' - ' : ' | ';
                    const siteTitle = document.title.split(separator).slice(1).join(separator);
                    document.title = siteTitle ? String(payload.title) + separator + siteTitle : String(payload.title);
                }

                if (options.pushState !== false) {
                    window.history.pushState({khProfileAction: normalizedAction}, '', targetUrl.toString());
                }

                if (options.scroll !== false) {
                    if (typeof window.khScrollElementBelowTopbar === 'function') {
                        window.khScrollElementBelowTopbar(panel, 'smooth');
                    } else {
                        panel.scrollIntoView({behavior: 'smooth', block: 'start'});
                    }
                }
            },
            error: function (xhr, textStatus) {
                if (textStatus === 'abort') {
                    return;
                }

                khSetProfileTabActiveState(khProfileTabActionFromUrl(window.location.href));
                notifier.alert(getApiMessage(xhr.responseJSON, khMessage('profileTabLoadError', 'The profile area could not be loaded.')));
            },
            complete: function () {
                if (activeRequest === request) {
                    activeRequest = null;
                    khSetProfileTabLoadingState(panel, false);
                }
            },
        });
        activeRequest = request;
    };

    jQuery(document).on('click', '[data-profile-tab-link]', function (event) {
        const action = String(this.getAttribute('data-profile-action') || khProfileTabActionFromUrl(this.href));
        if (!action || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        event.preventDefault();
        loadTab(action);
    });

    window.addEventListener('popstate', () => {
        loadTab(khProfileTabActionFromUrl(window.location.href), {
            pushState: false,
            scroll: false,
        });
    });
};

const khEscapeProfileSessionHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const khProfileSessionPayload = (payload) => {
    const data = payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object'
        ? payload.data
        : (payload || {});
    const sessions = Array.isArray(data.sessions) ? data.sessions : [];
    return {
        success: data.success !== false,
        max_sessions: parseInt(data.max_sessions || 3, 10) || 3,
        sessions,
        notification_settings: data.notification_settings && typeof data.notification_settings === 'object'
            ? data.notification_settings
            : {},
        revoked_current: data.revoked_current === true,
    };
};

const khProfileSessionNotificationPreference = (settings = {}) => {
    const preference = settings && typeof settings === 'object'
        ? String(settings.login_notification_preference || '')
        : '';

    return ['new_devices', 'every_login', 'never'].includes(preference)
        ? preference
        : 'new_devices';
};

const khRenderProfileSessionNotificationSettings = (root, settings = {}) => {
    if (!root) {
        return;
    }

    const preference = khProfileSessionNotificationPreference(settings);
    const knownDevices = Math.max(0, parseInt(settings.known_devices_count || 0, 10) || 0);
    root.dataset.profileSessionNotificationPreference = preference;

    root.querySelectorAll('[data-profile-session-notification-option]').forEach((input) => {
        input.checked = input.value === preference;
    });

    const knownDevicesElement = root.querySelector('[data-profile-session-known-devices]');
    if (knownDevicesElement) {
        knownDevicesElement.textContent = khMessage('profileSessionsKnownDevices', 'Known devices') + ': ' + knownDevices;
    }
};

const khFormatProfileSessionDate = (value) => {
    if (!value) {
        return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '-';
    }

    try {
        return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(date);
    } catch (error) {
        return date.toLocaleString();
    }
};

const khProfileSessionRedirect = (root) => {
    const logoutUrl = root && root.getAttribute('data-logout-url')
        ? root.getAttribute('data-logout-url')
        : '/teejit/my-profile/?action=logout';
    window.location.href = logoutUrl;
};

const khHydrateProfileSessionSpinners = (root) => {
    if (!root) {
        return;
    }

    root.querySelectorAll('[data-profile-sessions-loading-spinner]').forEach((spinner) => {
        if (spinner.innerHTML.trim() === '') {
            spinner.innerHTML = khRenderLoadingSvg(28);
        }
    });
};

const khSetProfileSessionsLoading = (root, isLoading) => {
    if (!root) {
        return;
    }

    khHydrateProfileSessionSpinners(root);
    if (isLoading) {
        khUpdateProfileTabLoaderPosition(root);
    }

    root.classList.toggle('is-loading', isLoading);
    root.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    root.querySelectorAll('button').forEach((button) => {
        button.disabled = isLoading;
    });
};

const khRenderProfileSessions = (root, payload) => {
    if (!root) {
        return;
    }

    const data = khProfileSessionPayload(payload);
    const list = root.querySelector('[data-profile-sessions-list]');
    const summary = root.querySelector('[data-profile-sessions-summary]');
    const resetButton = root.querySelector('[data-profile-sessions-reset-all]');
    if (!list || !summary) {
        return;
    }

    khRenderProfileSessionNotificationSettings(root, data.notification_settings);

    const activeCount = data.sessions.length;
    summary.innerHTML = '<span class="kh-profile-sessions__count">' + khEscapeProfileSessionHtml(activeCount + ' / ' + data.max_sessions + ' ' + khMessage('profileSessionsActiveLogins', 'active logins')) + '</span>';

    if (resetButton) {
        resetButton.disabled = activeCount === 0;
    }

    if (activeCount === 0) {
        list.innerHTML = '<div class="kh-profile-sessions__empty">' + khEscapeProfileSessionHtml(khMessage('profileSessionsNoSessions', 'No active logins were found.')) + '</div>';
        return;
    }

    list.innerHTML = data.sessions.map((session) => {
        const isCurrent = session && session.is_current === true;
        const deviceName = session && session.device_name ? session.device_name : khMessage('profileSessionsBrowser', 'Browser');
        const domain = session && session.frontend_domain ? session.frontend_domain : window.location.hostname;
        const ipAddress = session && session.ip_address ? session.ip_address : '';
        const lastActivity = khFormatProfileSessionDate(session && (session.last_activity_at || session.last_used_at || session.created_at));
        const createdAt = khFormatProfileSessionDate(session && session.created_at);
        const userAgent = session && session.user_agent ? session.user_agent : '';
        const sessionId = session && session.id ? String(session.id) : '';

        return [
            '<article class="kh-profile-session' + (isCurrent ? ' is-current' : '') + '">',
            '<div class="kh-profile-session__main">',
            '<div class="kh-profile-session__head">',
            '<div class="kh-profile-session__identity">',
            '<div class="kh-profile-session__icon"><span class="material-symbols-outlined" aria-hidden="true">devices</span></div>',
            '<div class="kh-profile-session__title-row">',
            '<h2>' + khEscapeProfileSessionHtml(deviceName) + '</h2>',
            isCurrent ? '<span class="kh-profile-session__badge">' + khEscapeProfileSessionHtml(khMessage('profileSessionsCurrent', 'Current device')) + '</span>' : '',
            '</div>',
            '</div>',
            '<div class="kh-profile-session__actions">',
            '<button type="button" class="btn btn-secondary" data-profile-session-revoke="' + khEscapeProfileSessionHtml(sessionId) + '">',
            '<span class="material-symbols-outlined" aria-hidden="true">logout</span>',
            '<span>' + khEscapeProfileSessionHtml(isCurrent ? khMessage('profileSessionsLogout', 'Logout') : khMessage('profileSessionsRemove', 'Remove')) + '</span>',
            '</button>',
            '</div>',
            '</div>',
            '<div class="kh-profile-session__meta">',
            '<div class="kh-profile-session__meta-item"><span class="kh-profile-session__meta-label">' + khEscapeProfileSessionHtml(khMessage('profileSessionsDomain', 'Domain')) + '</span><span class="kh-profile-session__meta-value">' + khEscapeProfileSessionHtml(domain) + '</span></div>',
            ipAddress ? '<div class="kh-profile-session__meta-item"><span class="kh-profile-session__meta-label">' + khEscapeProfileSessionHtml(khMessage('profileSessionsIp', 'IP')) + '</span><span class="kh-profile-session__meta-value">' + khEscapeProfileSessionHtml(ipAddress) + '</span></div>' : '',
            '<div class="kh-profile-session__meta-item"><span class="kh-profile-session__meta-label">' + khEscapeProfileSessionHtml(khMessage('profileSessionsLastActivity', 'Last activity')) + '</span><span class="kh-profile-session__meta-value">' + khEscapeProfileSessionHtml(lastActivity) + '</span></div>',
            '<div class="kh-profile-session__meta-item"><span class="kh-profile-session__meta-label">' + khEscapeProfileSessionHtml(khMessage('profileSessionsSignedIn', 'Signed in')) + '</span><span class="kh-profile-session__meta-value">' + khEscapeProfileSessionHtml(createdAt) + '</span></div>',
            userAgent ? '<div class="kh-profile-session__meta-item kh-profile-session__user-agent"><span class="kh-profile-session__meta-label">' + khEscapeProfileSessionHtml(khMessage('profileSessionsUserAgent', 'User-Agent')) + '</span><span class="kh-profile-session__meta-value">' + khEscapeProfileSessionHtml(userAgent) + '</span></div>' : '',
            '</div>',
            '</div>',
            '</article>',
        ].join('');
    }).join('');
};

const khLoadProfileSessions = (root) => {
    if (!root) {
        return null;
    }

    const notifier = createKhNotifier({
        labels: {
            info: khMessage('noticeProfile', 'Profile'),
            alert: khMessage('noticeProfile', 'Profile'),
        }
    });

    khSetProfileSessionsLoading(root, true);
    return khRestAjax({
        method: 'GET',
        path: 'user/sessions',
        success: function (data) {
            khRenderProfileSessions(root, data);
        },
        error: function (xhr) {
            notifier.alert(getApiMessage(xhr.responseJSON, khMessage('profileSessionsLoadError', 'Your active sessions could not be loaded.')));
        },
        complete: function () {
            khSetProfileSessionsLoading(root, false);
        }
    });
};

let khProfileSessionConfirmState = {
    button: null,
    onConfirm: null,
};

const khEnsureProfileSessionConfirmModal = () => {
    let modal = document.getElementById('kh-profile-session-confirm-modal');
    if (modal) {
        return modal;
    }

    modal = document.createElement('div');
    modal.id = 'kh-profile-session-confirm-modal';
    modal.className = 'kh-modal kh-profile-session-confirm-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
        <div class="kh-modal-card kh-profile-session-confirm-card" role="dialog" aria-modal="true" aria-labelledby="kh-profile-session-confirm-title">
            <div class="kh-modal-head">
                <h3 id="kh-profile-session-confirm-title" class="kh-panel-title"></h3>
                <button type="button" class="kh-modal-close" data-profile-session-confirm-cancel>${khMessage('close', 'Close')}</button>
            </div>
            <p class="kh-profile-session-confirm-text" data-profile-session-confirm-text></p>
            <div class="kh-actions kh-profile-session-confirm-actions">
                <button type="button" class="btn btn-secondary" data-profile-session-confirm-cancel></button>
                <button type="button" class="btn btn-primary" data-profile-session-confirm-submit></button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
        if (event.target === modal || event.target.closest('[data-profile-session-confirm-cancel]')) {
            khCloseProfileSessionConfirmModal();
        }
    });

    modal.querySelector('[data-profile-session-confirm-submit]').addEventListener('click', () => {
        const onConfirm = khProfileSessionConfirmState.onConfirm;
        khCloseProfileSessionConfirmModal(false);

        if (typeof onConfirm === 'function') {
            onConfirm();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
            khCloseProfileSessionConfirmModal();
        }
    });

    return modal;
};

const khOpenProfileSessionConfirmModal = (button, options, onConfirm) => {
    const modal = khEnsureProfileSessionConfirmModal();
    khProfileSessionConfirmState.button = button;
    khProfileSessionConfirmState.onConfirm = onConfirm;

    modal.querySelector('#kh-profile-session-confirm-title').textContent = options.title;
    modal.querySelector('[data-profile-session-confirm-text]').textContent = options.text;
    modal.querySelector('[data-profile-session-confirm-cancel].btn').textContent = khMessage('profileSessionsCancel', 'Cancel');
    modal.querySelector('[data-profile-session-confirm-submit]').textContent = options.confirmLabel;
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    modal.querySelector('[data-profile-session-confirm-cancel].btn').focus();
};

const khCloseProfileSessionConfirmModal = (restoreFocus = true) => {
    const modal = document.getElementById('kh-profile-session-confirm-modal');
    if (!modal) {
        return;
    }

    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    const button = khProfileSessionConfirmState.button;
    khProfileSessionConfirmState.button = null;
    khProfileSessionConfirmState.onConfirm = null;

    if (restoreFocus && button && document.body.contains(button)) {
        button.focus();
    }
};

const khRevokeProfileSession = (root, sessionId) => {
    if (!root || sessionId === '') {
        return;
    }

    const notifier = createKhNotifier({
        labels: {
            success: khMessage('noticeProfile', 'Profile'),
            alert: khMessage('noticeProfile', 'Profile'),
        }
    });

    khSetProfileSessionsLoading(root, true);
    khRestAjax({
        method: 'DELETE',
        path: 'user/sessions/' + encodeURIComponent(sessionId),
        success: function (data) {
            const payload = khProfileSessionPayload(data);
            notifier.success(khMessage('profileSessionsRevoked', 'The login was removed.'), khToastOptions({
                durations: {global: 3000}
            }));

            if (payload.revoked_current) {
                khProfileSessionRedirect(root);
                return;
            }

            khRenderProfileSessions(root, payload);
        },
        error: function (xhr) {
            notifier.alert(getApiMessage(xhr.responseJSON, khMessage('profileSessionsLoadError', 'Your active sessions could not be loaded.')));
        },
        complete: function () {
            khSetProfileSessionsLoading(root, false);
        }
    });
};

const khResetProfileSessions = (root) => {
    if (!root) {
        return;
    }

    const notifier = createKhNotifier({
        labels: {
            success: khMessage('noticeProfile', 'Profile'),
            alert: khMessage('noticeProfile', 'Profile'),
        }
    });

    khSetProfileSessionsLoading(root, true);
    khRestAjax({
        method: 'DELETE',
        path: 'user/sessions',
        success: function () {
            notifier.success(khMessage('profileSessionsReset', 'All logins were removed.'), khToastOptions({
                durations: {global: 3000}
            }));
            khProfileSessionRedirect(root);
        },
        error: function (xhr) {
            notifier.alert(getApiMessage(xhr.responseJSON, khMessage('profileSessionsLoadError', 'Your active sessions could not be loaded.')));
        },
        complete: function () {
            khSetProfileSessionsLoading(root, false);
        }
    });
};

const khSaveProfileSessionNotificationSettings = (root) => {
    if (!root) {
        return;
    }

    const selected = root.querySelector('[data-profile-session-notification-option]:checked');
    const preference = selected ? String(selected.value || '') : '';
    if (!['new_devices', 'every_login', 'never'].includes(preference)) {
        return;
    }

    const notifier = createKhNotifier({
        labels: {
            success: khMessage('noticeProfile', 'Profile'),
            alert: khMessage('noticeProfile', 'Profile'),
        }
    });

    khSetProfileSessionsLoading(root, true);
    khRestAjax({
        method: 'POST',
        path: 'user/sessions/notification-settings',
        data: {
            login_notification_preference: preference,
        },
        success: function (data) {
            const payload = khProfileSessionPayload(data);
            khRenderProfileSessionNotificationSettings(root, payload.notification_settings);
            notifier.success(khMessage('profileSessionsNotificationSaved', 'Your notification setting was saved.'), khToastOptions({
                durations: {global: 3000}
            }));
        },
        error: function (xhr) {
            notifier.alert(getApiMessage(xhr.responseJSON, khMessage('profileSessionsNotificationSaveError', 'Your notification setting could not be saved.')));
        },
        complete: function () {
            khSetProfileSessionsLoading(root, false);
        }
    });
};

const khBootProfileSessions = (scope = document) => {
    const rootScope = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    rootScope.querySelectorAll('[data-profile-sessions]').forEach((root) => {
        if (root.dataset.profileSessionsBooted === '1') {
            return;
        }

        root.dataset.profileSessionsBooted = '1';
        khHydrateProfileSessionSpinners(root);

        let loaderPositionFrame = null;
        const updateSessionLoaderPosition = () => {
            if (!root.classList.contains('is-loading')) {
                return;
            }

            if (loaderPositionFrame) {
                window.cancelAnimationFrame(loaderPositionFrame);
            }

            loaderPositionFrame = window.requestAnimationFrame(() => {
                loaderPositionFrame = null;
                khUpdateProfileTabLoaderPosition(root);
            });
        };

        window.addEventListener('scroll', updateSessionLoaderPosition, {passive: true});
        window.addEventListener('resize', updateSessionLoaderPosition);

        const initial = root.getAttribute('data-initial-sessions');
        let shouldRefresh = true;
        if (initial) {
            try {
                const initialPayload = JSON.parse(initial);
                khRenderProfileSessions(root, initialPayload);
                shouldRefresh = khProfileSessionPayload(initialPayload).success === false;
            } catch (error) {
                khRenderProfileSessions(root, {sessions: []});
            }
        }
        if (shouldRefresh) {
            khLoadProfileSessions(root);
        }
    });
};

jQuery(document).on('click', '[data-profile-sessions-refresh]', function () {
    khLoadProfileSessions(this.closest('[data-profile-sessions]'));
});

jQuery(document).on('click', '[data-profile-session-revoke]', function () {
    const button = this;
    const root = button.closest('[data-profile-sessions]');
    const sessionId = String(button.getAttribute('data-profile-session-revoke') || '');
    if (!root || sessionId === '') {
        return;
    }

    khOpenProfileSessionConfirmModal(button, {
        title: khMessage('profileSessionsRevokeTitle', 'Remove login?'),
        text: khMessage('profileSessionsRevokeConfirm', 'Do you really want to remove this login?'),
        confirmLabel: khMessage('profileSessionsConfirmRemove', 'Remove login'),
    }, () => {
        khRevokeProfileSession(root, sessionId);
    });
});

jQuery(document).on('click', '[data-profile-sessions-reset-all]', function () {
    const root = this.closest('[data-profile-sessions]');
    if (!root) {
        return;
    }

    khOpenProfileSessionConfirmModal(this, {
        title: khMessage('profileSessionsResetAllTitle', 'Reset all logins?'),
        text: khMessage('profileSessionsResetAllConfirm', 'Do you really want to remove all logins? You will need to sign in again.'),
        confirmLabel: khMessage('profileSessionsConfirmResetAll', 'Reset all logins'),
    }, () => {
        khResetProfileSessions(root);
    });
});

jQuery(document).on('click', '[data-profile-session-notification-save]', function () {
    khSaveProfileSessionNotificationSettings(this.closest('[data-profile-sessions]'));
});

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        khBootProfileTabs();
        khBootProfileSessions(document);
    }, {once: true});
} else {
    khBootProfileTabs();
    khBootProfileSessions(document);
}

const khNotificationIconName = (icon) => {
    const value = String(icon || '').trim();
    if (!value || value.indexOf('heroicon-') === 0) {
        return 'notifications';
    }

    return value.replace(/^material-symbols?:/, '') || 'notifications';
};

const khBootNotifications = () => {
    const root = document.querySelector('[data-kh-notifications]');
    if (!root || !khUserAccessToken) {
        return;
    }

    const toggle = root.querySelector('.t-nav-notifications-toggle');
    const panel = root.querySelector('.t-nav-notifications-panel');
    const list = root.querySelector('[data-kh-notifications-list]');
    const badge = root.querySelector('[data-kh-notification-count]');
    const readAllButton = root.querySelector('[data-kh-notifications-read-all]');
    if (!toggle || !panel || !list || !badge) {
        return;
    }

    let notifications = [];
    let isLoading = false;
    let notificationsDisabled = false;
    let notificationPollTimer = null;
    let remoteNotificationsLoadedOnce = false;
    const deferStartupNotifications = true;

    const getPayload = (data) => {
        if (data && typeof data === 'object' && typeof data.data !== 'undefined') {
            return data.data;
        }

        return data;
    };

    const setOpen = (isOpen) => {
        root.classList.toggle('is-open', isOpen);
        panel.hidden = !isOpen;
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (isOpen) {
            loadNotifications();
        }
    };

    const setBadge = (count) => {
        const unreadCount = Math.max(0, parseInt(count, 10) || 0);
        badge.textContent = unreadCount > 99 ? '99+' : String(unreadCount);
        badge.hidden = unreadCount === 0;
        if (readAllButton) {
            readAllButton.hidden = unreadCount === 0;
        }
    };

    const disableNotificationPolling = () => {
        notificationsDisabled = true;
        notifications = [];
        setBadge(0);
        if (notificationPollTimer) {
            window.clearInterval(notificationPollTimer);
            notificationPollTimer = null;
        }
        renderEmpty(khMessage('notificationsEmpty', 'No notifications yet.'));
    };

    const applyBootstrapNotificationSummary = (payload) => {
        const bootstrapPayload = getPayload(payload) || {};
        const summary = bootstrapPayload.notifications
            || bootstrapPayload.notification_summary
            || bootstrapPayload.notificationSummary
            || {};
        const count = summary.unread_count
            ?? summary.unreadCount
            ?? bootstrapPayload.unread_notifications_count
            ?? bootstrapPayload.unreadNotificationsCount;

        if (typeof count === 'undefined' || count === null || count === '') {
            return false;
        }

        const localUnreadCount = khReadLocalNotifications().filter((notification) => !notification.read_at).length;
        setBadge((parseInt(count, 10) || 0) + localUnreadCount);
        return true;
    };

    const renderEmpty = (message) => {
        list.innerHTML = '';
        const empty = document.createElement('div');
        empty.className = 't-nav-notifications-empty';
        empty.textContent = message;
        list.appendChild(empty);
    };

    const renderLoading = () => {
        list.innerHTML = '';
        const loading = document.createElement('div');
        loading.className = 't-nav-notifications-loading';
        loading.innerHTML = khRenderLoadingSvg(26) + '<span>' + khMessage('notificationsLoading', 'Loading notifications...') + '</span>';
        list.appendChild(loading);
    };

    const renderNotifications = () => {
        if (!notifications.length) {
            renderEmpty(khMessage('notificationsEmpty', 'No notifications yet.'));
            return;
        }

        list.innerHTML = '';
        notifications.forEach((notification) => {
            const item = document.createElement('button');
            item.type = 'button';
            item.className = 't-nav-notification-item';
            item.dataset.notificationId = String(notification.id || '');
            item.dataset.notificationUrl = String(notification.url || '');
            if (!notification.read_at) {
                item.classList.add('is-unread');
            }

            const icon = document.createElement('span');
            icon.className = 't-nav-notification-icon';
            icon.setAttribute('aria-hidden', 'true');
            const iconSymbol = document.createElement('span');
            iconSymbol.className = 'material-symbols-outlined';
            iconSymbol.textContent = khNotificationIconName(notification.icon);
            icon.appendChild(iconSymbol);

            const title = document.createElement('span');
            title.className = 't-nav-notification-title';
            title.textContent = String(notification.title || khMessage('notificationsTitleFallback', 'Notification'));

            const header = document.createElement('span');
            header.className = 't-nav-notification-header';
            header.appendChild(icon);
            header.appendChild(title);

            const body = document.createElement('span');
            body.className = 't-nav-notification-body';

            if (notification.message) {
                const message = document.createElement('span');
                message.className = 't-nav-notification-message';
                message.textContent = String(notification.message);
                body.appendChild(message);
            }

            item.appendChild(header);
            item.appendChild(body);
            list.appendChild(item);
        });
    };

    const applyPayload = (data) => {
        const payload = getPayload(data) || {};
        const localNotifications = khReadLocalNotifications();
        const remoteNotifications = Array.isArray(payload.notifications) ? payload.notifications : [];
        const remoteIds = new Set(remoteNotifications.map((notification) => String(notification.id || '')));
        notifications = localNotifications
            .filter((notification) => !remoteIds.has(String(notification.id || '')))
            .concat(remoteNotifications);
        const localUnreadCount = localNotifications.filter((notification) => !notification.read_at).length;
        setBadge((parseInt(payload.unread_count, 10) || 0) + localUnreadCount);
        renderNotifications();
    };

    const loadNotifications = () => {
        if (isLoading || notificationsDisabled) {
            return;
        }

        isLoading = true;
        if (!notifications.length) {
            renderLoading();
        }

        khUserRestClient.requestJson('user/notifications?limit=20')
            .then((data) => {
                remoteNotificationsLoadedOnce = true;
                applyPayload(data);
            })
            .catch((error) => {
                if (error && (error.status === 401 || error.status === 403)) {
                    disableNotificationPolling();
                    return;
                }

                if (!notifications.length) {
                    renderEmpty(khMessage('notificationsLoadError', 'Notifications could not be loaded.'));
                }
            })
            .finally(() => {
                isLoading = false;
            });
    };

    const scheduleNotificationsLoad = () => {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(loadNotifications, {timeout: 2500});
            return;
        }

        window.setTimeout(loadNotifications, 1200);
    };

    const loadNotificationsAfterLocalChange = () => {
        if (deferStartupNotifications && !remoteNotificationsLoadedOnce && panel.hidden) {
            if (applyBootstrapNotificationSummary(khGetHomeBootstrapPayload())) {
                return;
            }
        }

        loadNotifications();
    };

    const loadNotificationsAfterFocus = () => {
        if (deferStartupNotifications && !remoteNotificationsLoadedOnce && panel.hidden) {
            return;
        }

        loadNotifications();
    };

    const markRead = (notificationId, callback) => {
        if (!notificationId) {
            if (typeof callback === 'function') {
                callback();
            }
            return;
        }

        if (String(notificationId).indexOf(khLocalNotificationPrefix) === 0) {
            khMarkLocalNotificationRead(notificationId);
            notifications = notifications.map((notification) => {
                if (String(notification.id) !== String(notificationId)) {
                    return notification;
                }

                return Object.assign({}, notification, {read_at: notification.read_at || new Date().toISOString()});
            });
            setBadge(notifications.filter((notification) => !notification.read_at).length);
            renderNotifications();
            loadNotifications();

            if (typeof callback === 'function') {
                callback();
            }
            return;
        }

        khRestAjax({
            method: 'POST',
            path: 'user/notifications/' + encodeURIComponent(notificationId) + '/read',
            success: (data) => {
                const payload = getPayload(data) || {};
                notifications = notifications.map((notification) => {
                    if (String(notification.id) !== String(notificationId)) {
                        return notification;
                    }

                    return Object.assign({}, notification, {read_at: notification.read_at || new Date().toISOString()});
                });
                setBadge(typeof payload.unread_count !== 'undefined'
                    ? payload.unread_count
                    : notifications.filter((notification) => !notification.read_at).length
                );
                renderNotifications();
                loadNotifications();
            },
            complete: callback,
        });
    };

    toggle.addEventListener('click', (event) => {
        event.preventDefault();
        const shouldOpen = panel.hidden;
        if (shouldOpen) {
            document.dispatchEvent(new CustomEvent('kh:topbar-menu-open', {
                detail: {
                    source: 'notifications',
                },
            }));
        }
        setOpen(shouldOpen);
    });

    list.addEventListener('click', (event) => {
        const item = event.target.closest('.t-nav-notification-item');
        if (!item) {
            return;
        }

        const url = item.dataset.notificationUrl || '';
        const id = item.dataset.notificationId || '';
        markRead(id, () => {
            if (url) {
                window.location.href = url;
            }
        });
    });

    if (readAllButton) {
        readAllButton.addEventListener('click', (event) => {
            event.preventDefault();
            khMarkAllLocalNotificationsRead();
            notifications = notifications.map((notification) => Object.assign({}, notification, {
                read_at: notification.read_at || new Date().toISOString(),
            }));
            setBadge(0);
            renderNotifications();

            khRestAjax({
                method: 'POST',
                path: 'user/notifications/read-all',
                success: (data) => {
                    notifications = notifications.map((notification) => Object.assign({}, notification, {
                        read_at: notification.read_at || new Date().toISOString(),
                    }));
                    setBadge((getPayload(data) || {}).unread_count || 0);
                    renderNotifications();
                },
            });
        });
    }

    document.addEventListener('click', (event) => {
        if (!root.contains(event.target)) {
            setOpen(false);
        }
    });

    document.addEventListener('kh:topbar-menu-open', (event) => {
        if (event.detail?.source !== 'notifications') {
            setOpen(false);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            setOpen(false);
        }
    });

    window.addEventListener('kh:homeBootstrapReady', (event) => {
        applyBootstrapNotificationSummary(event.detail);
    });
    applyBootstrapNotificationSummary(khGetHomeBootstrapPayload());

    document.addEventListener('kh:notifications-local-updated', loadNotificationsAfterLocalChange);
    window.addEventListener('focus', loadNotificationsAfterFocus);
    if (!deferStartupNotifications) {
        scheduleNotificationsLoad();
    }
    notificationPollTimer = window.setInterval(loadNotifications, 60000);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', khBootNotifications, {once: true});
} else {
    khBootNotifications();
}

const getApiPayload = (data) => {
    if (data && typeof data === 'object' && typeof data.data !== 'undefined') {
        return data.data;
    }

    return data;
};

const khNormalizeTopNavLabelMode = (value) => {
    const normalized = String(value ?? '').trim().toLowerCase();
    if (['hover', 'on_hover', 'icon', 'icons', 'hidden', '0', 'false'].includes(normalized) || value === false || value === 0) {
        return 'hover';
    }
    if (['always', 'visible', 'show', 'shown', '1', 'true'].includes(normalized) || value === true || value === 1) {
        return 'always';
    }

    return 'always';
};

const khTopNavLabelPreferenceStorageKey = () => {
    const userId = String(khRuntime.userId || window.khThemeRuntime?.userId || 'guest');
    return 'khTopNavLabelPreference:' + userId;
};

const khReadStoredTopNavLabelPreference = () => {
    try {
        return window.localStorage.getItem(khTopNavLabelPreferenceStorageKey());
    } catch (error) {
        return null;
    }
};

const khStoreTopNavLabelPreference = (mode) => {
    try {
        window.localStorage.setItem(khTopNavLabelPreferenceStorageKey(), mode);
    } catch (error) {
        // Ignore storage restrictions.
    }
};

const khApplyTopNavLabelPreference = (labelMode, persist = false) => {
    const mode = khNormalizeTopNavLabelMode(labelMode);
    const isHoverMode = mode === 'hover';
    document.body.classList.toggle('kh-nav-labels-visible', !isHoverMode);
    document.body.classList.toggle('kh-nav-labels-hidden', isHoverMode);
    document.body.classList.toggle('kh-nav-labels-hover', isHoverMode);
    if (persist) {
        khStoreTopNavLabelPreference(mode);
    }
};

window.khApplyTopNavLabelPreference = khApplyTopNavLabelPreference;

const khExtractTopNavLabelPreference = (data) => {
    const payload = getApiPayload(data);
    const settings = payload?.homepage?.settings || payload?.settings || {};

    if (Object.prototype.hasOwnProperty.call(settings, 'top_nav_label_mode')) {
        return settings.top_nav_label_mode;
    }
    if (Object.prototype.hasOwnProperty.call(settings, 'topNavLabelMode')) {
        return settings.topNavLabelMode;
    }
    if (Object.prototype.hasOwnProperty.call(settings, 'show_top_nav_labels')) {
        return settings.show_top_nav_labels;
    }
    if (Object.prototype.hasOwnProperty.call(settings, 'showTopNavLabels')) {
        return settings.showTopNavLabels;
    }

    return null;
};

const khLoadTopNavLabelPreference = () => {
    if (!document.querySelector('.t-nav-head-apps')) {
        return;
    }

    const storedPreference = khReadStoredTopNavLabelPreference();
    khApplyTopNavLabelPreference(storedPreference !== null ? storedPreference : true);

    const bootstrapPreference = khExtractTopNavLabelPreference(khGetHomeBootstrapPayload());
    if (bootstrapPreference !== null) {
        khApplyTopNavLabelPreference(bootstrapPreference, true);
        return;
    }

    if (khIsHomepageBootstrapActive()) {
        window.addEventListener('kh:homeBootstrapReady', (event) => {
            const preference = khExtractTopNavLabelPreference(event.detail);
            if (preference !== null) {
                khApplyTopNavLabelPreference(preference, true);
            }
        }, {once: true});
        return;
    }
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', khLoadTopNavLabelPreference, {once: true});
} else {
    khLoadTopNavLabelPreference();
}

const isApiSuccess = (data) => {
    const payload = getApiPayload(data);

    if (payload && typeof payload === 'object') {
        if (typeof payload.status !== 'undefined') {
            const status = String(payload.status).toLowerCase();
            return ['true', 'success', 'ok', 'pending', 'waitlist', 'waitlisted', 'waiting'].includes(status);
        }

        if (typeof payload.success !== 'undefined') {
            return payload.success === true || payload.success === 'true';
        }

        const waitlistKeys = [
            'waitlist',
            'waiting_list',
            'on_waiting_list',
            'is_waiting_list',
            'requires_approval',
            'approval_required',
            'waitingList',
            'onWaitingList',
            'isWaitingList',
            'requiresApproval',
            'approvalRequired'
        ];
        if (waitlistKeys.some((key) => !!payload[key])) {
            return true;
        }
    }

    return data && data.success === true && payload !== false;
};

const getApiMessage = (data, fallback) => {
    const payload = getApiPayload(data);

    if (payload && typeof payload === 'object' && typeof payload.message === 'string' && payload.message.trim()) {
        return payload.message;
    }

    return fallback;
};

const khCollectResponseMessages = (value, messages = []) => {
    if (!value || typeof value !== 'object') {
        return messages;
    }

    Object.entries(value).forEach(([key, item]) => {
        if (typeof item === 'string' && ['message', 'error', 'errors', 'notice'].includes(String(key || '').toLowerCase()) && item.trim()) {
            messages.push(item);
            return;
        }

        if (Array.isArray(item)) {
            item.forEach((nested) => khCollectResponseMessages(nested, messages));
            return;
        }

        if (item && typeof item === 'object') {
            khCollectResponseMessages(item, messages);
        }
    });

    return messages;
};

const khResponseHasErrorMessage = (data) => {
    const messages = khCollectResponseMessages(data);
    return messages.some((message) => {
        const normalized = String(message || '').toLowerCase();
        return /(fehler|error|nicht|kannst|kann nicht|cannot|can't|unable|failed|forbidden|denied|rolle|rollen|role|permission|berechtigung)/.test(normalized);
    });
};

const isChannelLeaveSuccessResponse = (data) => {
    if (khResponseHasErrorMessage(data)) {
        return false;
    }

    const payload = getApiPayload(data);
    if (!payload || typeof payload !== 'object') {
        return false;
    }

    if (Object.prototype.hasOwnProperty.call(payload, 'status')) {
        const status = payload.status;
        if (status === true || status === 1) {
            return true;
        }

        if (typeof status === 'string') {
            return ['true', 'success', 'ok'].includes(status.trim().toLowerCase());
        }

        return false;
    }

    if (Object.prototype.hasOwnProperty.call(payload, 'success')) {
        const success = payload.success;
        return success === true || success === 1 || (typeof success === 'string' && ['true', 'success', 'ok'].includes(success.trim().toLowerCase()));
    }

    return false;
};

const khProfileAjaxPaths = {
    personal: 'user/profile/personal',
    work: 'user/profile/work',
    audience: 'user/profile/audience',
    password: 'user/profile/password',
};

const khProfileFormData = (form) => {
    const data = {};
    jQuery(form).serializeArray().forEach((field) => {
        const isProviderSyncTeamField = field.name === 'kh_profile_sync_team_ids[]' || field.name === 'kh_profile_sync_team_ids';
        if (!field.name || (field.name.indexOf('kh_') === 0 && !isProviderSyncTeamField)) {
            return;
        }

        const fieldName = isProviderSyncTeamField ? 'kh_profile_sync_team_ids' : field.name;

        if (Object.prototype.hasOwnProperty.call(data, fieldName)) {
            if (!Array.isArray(data[fieldName])) {
                data[fieldName] = [data[fieldName]];
            }
            data[fieldName].push(field.value);
            return;
        }

        data[fieldName] = field.value;
    });

    jQuery(form).find('input[type="checkbox"][data-profile-boolean-field]').each(function () {
        const field = this;
        if (!field.name || field.name.indexOf('kh_') === 0) {
            return;
        }

        data[field.name] = field.checked ? '1' : '0';
    });

    return data;
};

const khProfileSetImagePreview = (editor, previewUrl) => {
    const preview = jQuery(editor).find('[data-profile-image-preview]').first();
    if (!preview.length || !previewUrl) {
        return;
    }

    preview.empty().append(jQuery('<img>', {
        src: previewUrl,
        alt: '',
        width: 112,
        height: 112,
    }));

    updateTeejitProfileAvatarNodes(previewUrl);
};

const khProfileSetImageValue = (editor, value, previewUrl) => {
    const root = jQuery(editor);
    root.find('[data-profile-image-field]').val(value);
    khProfileSetImagePreview(root, previewUrl || value);
};

const khProfileViewUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('edit');
    return url.toString();
};

const khSetProfileButtonLoading = (button, isLoading) => {
    if (!button.length) {
        return;
    }

    if (isLoading) {
        if (typeof button.data('profile-original-html') === 'undefined') {
            button.data('profile-original-html', button.html());
        }

        const buttonWidth = button.outerWidth();
        const buttonHeight = button.outerHeight();

        let spinner = button.siblings('.profile-save-spinner').first();
        if (!spinner.length) {
            spinner = jQuery('<span>', {
                class: 'profile-save-spinner',
                role: 'status',
                'aria-live': 'polite',
            });
            button.after(spinner);
        }
        spinner.css({
            display: 'flex',
            height: buttonHeight,
            width: buttonWidth,
        });
        button.prop('disabled', true).hide();
        spinner.empty();
        spinner.html(khRenderLoadingSvg(28));

        return;
    }

    const originalHtml = button.data('profile-original-html');
    if (typeof originalHtml !== 'undefined') {
        button.html(originalHtml);
    }

    button.prop('disabled', false).show();
    button.siblings('.profile-save-spinner').first().removeAttr('style').hide().empty();
};

const khAudienceLinkedSelector = '[data-profile-audience-form] [data-audience-checkbox]';
let khAudienceLinkedModalSource = null;
let khAudienceLinkedModalGroup = [];

const khAudienceGetLinkedGroup = (sourceCheckbox) => {
    const form = sourceCheckbox.closest('[data-profile-audience-form]');
    const originalId = String(sourceCheckbox.dataset.audienceOriginalId || '').trim();
    if (!form || !originalId) {
        return [];
    }

    return Array.from(form.querySelectorAll(khAudienceLinkedSelector)).filter((checkbox) => (
        String(checkbox.dataset.audienceOriginalId || '').trim() === originalId
    ));
};

const khAudienceSetChecked = (checkbox, checked) => {
    checkbox.dataset.audienceLinkedSilent = '1';
    checkbox.checked = checked;
    checkbox.dispatchEvent(new Event('change', {bubbles: true}));
    delete checkbox.dataset.audienceLinkedSilent;
};

const khAudienceEnsureLinkedModal = () => {
    let modal = document.getElementById('kh-profile-linked-attributes-modal');
    if (modal) {
        return modal;
    }

    modal = document.createElement('div');
    modal.id = 'kh-profile-linked-attributes-modal';
    modal.className = 'kh-modal kh-profile-linked-attributes-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
        <div class="kh-modal-card kh-profile-linked-attributes-card" role="dialog" aria-modal="true" aria-labelledby="kh-profile-linked-attributes-title">
            <div class="kh-modal-head">
                <h3 id="kh-profile-linked-attributes-title" class="kh-panel-title"></h3>
                <button type="button" class="kh-modal-close" data-profile-linked-close>${khMessage('close', 'Close')}</button>
            </div>
            <p class="kh-profile-linked-attributes-prompt"></p>
            <div class="kh-profile-linked-attributes-options" data-profile-linked-options></div>
            <div class="kh-profile-linked-attributes-list" data-profile-linked-list hidden></div>
            <div class="kh-actions kh-profile-linked-attributes-actions">
                <button type="button" class="btn btn-primary" data-profile-linked-select-all></button>
                <button type="button" class="btn btn-secondary" data-profile-linked-show-list></button>
                <button type="button" class="btn btn-primary" data-profile-linked-apply hidden></button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
        if (event.target === modal || event.target.closest('[data-profile-linked-close]')) {
            khAudienceCloseLinkedModal();
        }
    });

    modal.querySelector('[data-profile-linked-select-all]').addEventListener('click', () => {
        khAudienceLinkedModalGroup.forEach((checkbox) => khAudienceSetChecked(checkbox, true));
        khAudienceCloseLinkedModal();
    });

    modal.querySelector('[data-profile-linked-show-list]').addEventListener('click', () => {
        khAudienceRenderLinkedList(modal);
    });

    modal.querySelector('[data-profile-linked-apply]').addEventListener('click', () => {
        modal.querySelectorAll('[data-profile-linked-choice]').forEach((choice) => {
            const index = parseInt(choice.dataset.profileLinkedChoice || '-1', 10);
            const checkbox = khAudienceLinkedModalGroup[index];
            if (!checkbox) {
                return;
            }

            khAudienceSetChecked(checkbox, checkbox === khAudienceLinkedModalSource || choice.checked);
        });
        khAudienceCloseLinkedModal();
    });

    return modal;
};

const khAudienceOpenLinkedModal = (sourceCheckbox, linkedGroup) => {
    const modal = khAudienceEnsureLinkedModal();
    khAudienceLinkedModalSource = sourceCheckbox;
    khAudienceLinkedModalGroup = linkedGroup;

    modal.querySelector('#kh-profile-linked-attributes-title').textContent = khMessage('profileLinkedAttributesTitle', 'Linked attributes');
    modal.querySelector('.kh-profile-linked-attributes-prompt').textContent = khMessage(
        'profileLinkedAttributesPrompt',
        'This attribute is linked to other attributes from other channels. Would you like to select the linked attributes as well?'
    );
    modal.querySelector('[data-profile-linked-select-all]').textContent = khMessage('profileLinkedAttributesSelectAll', 'Select all');
    modal.querySelector('[data-profile-linked-show-list]').textContent = khMessage('profileLinkedAttributesSelectByChannel', 'Select individually by channel');
    modal.querySelector('[data-profile-linked-apply]').textContent = khMessage('profileLinkedAttributesApply', 'Apply selection');
    modal.querySelector('[data-profile-linked-options]').hidden = false;
    modal.querySelector('[data-profile-linked-list]').hidden = true;
    modal.querySelector('[data-profile-linked-list]').innerHTML = '';
    modal.querySelector('[data-profile-linked-show-list]').hidden = false;
    modal.querySelector('[data-profile-linked-apply]').hidden = true;
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    modal.querySelector('[data-profile-linked-select-all]').focus();
};

const khAudienceCloseLinkedModal = () => {
    const modal = document.getElementById('kh-profile-linked-attributes-modal');
    if (!modal) {
        return;
    }

    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (khAudienceLinkedModalSource) {
        khAudienceLinkedModalSource.focus();
    }
    khAudienceLinkedModalSource = null;
    khAudienceLinkedModalGroup = [];
};

const khAudienceRenderLinkedList = (modal) => {
    const list = modal.querySelector('[data-profile-linked-list]');
    const options = modal.querySelector('[data-profile-linked-options]');
    const applyButton = modal.querySelector('[data-profile-linked-apply]');
    const byTeam = new Map();

    khAudienceLinkedModalGroup.forEach((checkbox, index) => {
        const teamName = String(checkbox.dataset.audienceTeamName || '').trim()
            || khMessage('profileLinkedAttributesCurrentChannel', 'Current channel');
        if (!byTeam.has(teamName)) {
            byTeam.set(teamName, []);
        }
        byTeam.get(teamName).push({checkbox, index});
    });

    list.innerHTML = '';
    byTeam.forEach((items, teamName) => {
        const group = document.createElement('section');
        group.className = 'kh-profile-linked-attributes-channel';

        const title = document.createElement('h4');
        title.textContent = teamName;
        group.appendChild(title);

        items.forEach(({checkbox, index}) => {
            const item = document.createElement('label');
            item.className = 'kh-profile-linked-attributes-choice';

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.dataset.profileLinkedChoice = String(index);
            input.checked = checkbox.checked || checkbox === khAudienceLinkedModalSource;
            input.disabled = checkbox === khAudienceLinkedModalSource;

            const label = document.createElement('span');
            label.textContent = String(checkbox.dataset.audienceTagName || checkbox.value || '');

            item.append(input, label);
            group.appendChild(item);
        });

        list.appendChild(group);
    });

    options.hidden = true;
    list.hidden = false;
    modal.querySelector('[data-profile-linked-show-list]').hidden = true;
    applyButton.hidden = false;
    applyButton.focus();
};

const khProfileProviderSyncFieldSelector = '[data-profile-provider-sync]';
let khProfileProviderSyncModalState = {
    form: null,
};

const khProfileGetProviderSyncConfig = (formEl) => {
    if (!formEl) {
        return null;
    }

    if (formEl._khProfileProviderSyncConfig) {
        return formEl._khProfileProviderSyncConfig;
    }

    const raw = String(formEl.getAttribute('data-profile-provider-sync') || '').trim();
    if (!raw) {
        return null;
    }

    try {
        formEl._khProfileProviderSyncConfig = JSON.parse(raw);
    } catch (error) {
        formEl._khProfileProviderSyncConfig = null;
    }

    return formEl._khProfileProviderSyncConfig;
};

const khProfileGetFieldValue = (field) => {
    if (!field) {
        return '';
    }

    if (field.type === 'checkbox') {
        return field.checked ? '1' : '0';
    }

    if (field.type === 'radio') {
        const checked = field.form ? field.form.querySelector(`[name="${field.name}"]:checked`) : null;
        return checked ? String(checked.value || '') : '';
    }

    return String(field.value || '');
};

const khProfileHasProviderSyncSelection = (formEl) => {
    return !!(formEl && formEl.querySelector('input[name="kh_profile_sync_team_ids[]"]'));
};

const khProfileClearProviderSyncSelection = (formEl) => {
    if (!formEl) {
        return;
    }

    formEl.querySelectorAll('input[name="kh_profile_sync_team_ids[]"]').forEach((input) => input.remove());
};

const khProfileAppendProviderSyncSelection = (formEl, teamIds) => {
    khProfileClearProviderSyncSelection(formEl);

    teamIds.forEach((teamId) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'kh_profile_sync_team_ids[]';
        input.value = String(teamId);
        formEl.appendChild(input);
    });
};

const khProfileCollectProviderSyncChanges = (formEl) => {
    const config = khProfileGetProviderSyncConfig(formEl);
    if (!config || !config.field_map) {
        return null;
    }

    const changedFields = [];
    const affectedTeams = new Map();

    Object.entries(config.field_map).forEach(([userFieldKey, targets]) => {
        const field = formEl.querySelector(`[name="${userFieldKey}"]`);
        if (!field) {
            return;
        }

        const currentValue = khProfileGetFieldValue(field);
        const initialValue = String((config.current_values && config.current_values[userFieldKey]) || '');
        if (currentValue === initialValue) {
            return;
        }

        const firstTarget = Array.isArray(targets) ? targets[0] : null;
        changedFields.push({
            key: userFieldKey,
            label: firstTarget && firstTarget.field_label ? String(firstTarget.field_label) : userFieldKey,
        });

        (Array.isArray(targets) ? targets : []).forEach((target) => {
            const teamId = String(target.team_id || '').trim();
            if (!teamId) {
                return;
            }

            affectedTeams.set(teamId, {
                team_id: parseInt(teamId, 10),
                team_name: String(target.team_name || ''),
                provider_name: String(target.provider_name || ''),
            });
        });
    });

    if (!changedFields.length || !affectedTeams.size) {
        return null;
    }

    return {
        changedFields,
        teams: Array.from(affectedTeams.values()),
    };
};

const khProfileEnsureProviderSyncModal = () => {
    let modal = document.getElementById('kh-profile-provider-sync-modal');
    if (modal) {
        return modal;
    }

    modal = document.createElement('div');
    modal.id = 'kh-profile-provider-sync-modal';
    modal.className = 'kh-modal kh-profile-provider-sync-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
        <div class="kh-modal-card kh-profile-provider-sync-card" role="dialog" aria-modal="true" aria-labelledby="kh-profile-provider-sync-title">
            <div class="kh-modal-head">
                <h3 id="kh-profile-provider-sync-title" class="kh-panel-title"></h3>
                <button type="button" class="kh-modal-close" data-profile-provider-sync-close>${khMessage('close', 'Close')}</button>
            </div>
            <p class="kh-profile-provider-sync-prompt"></p>
            <div class="kh-profile-provider-sync-fields"></div>
            <div class="kh-profile-provider-sync-groups"></div>
            <div class="kh-actions kh-profile-provider-sync-actions">
                <button type="button" class="btn btn-secondary" data-profile-provider-sync-skip></button>
                <button type="button" class="btn btn-primary" data-profile-provider-sync-apply></button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
        if (event.target === modal || event.target.closest('[data-profile-provider-sync-close]')) {
            khProfileCloseProviderSyncModal();
        }
    });

    modal.querySelector('[data-profile-provider-sync-skip]').addEventListener('click', () => {
        const formEl = khProfileProviderSyncModalState.form;
        khProfileCloseProviderSyncModal();
        if (!formEl) {
            return;
        }

        khProfileClearProviderSyncSelection(formEl);
        formEl.dataset.profileSyncConfirmed = '1';
        jQuery(formEl).trigger('submit');
    });

    modal.querySelector('[data-profile-provider-sync-apply]').addEventListener('click', () => {
        const formEl = khProfileProviderSyncModalState.form;
        if (!formEl) {
            khProfileCloseProviderSyncModal();
            return;
        }

        const selectedTeamIds = Array.from(modal.querySelectorAll('[data-profile-provider-sync-choice]:checked'))
            .map((input) => parseInt(input.value, 10))
            .filter((value) => Number.isInteger(value) && value > 0);

        khProfileAppendProviderSyncSelection(formEl, selectedTeamIds);
        khProfileCloseProviderSyncModal();
        formEl.dataset.profileSyncConfirmed = '1';
        jQuery(formEl).trigger('submit');
    });

    return modal;
};

const khProfileOpenProviderSyncModal = (formEl, syncMeta) => {
    const modal = khProfileEnsureProviderSyncModal();
    khProfileProviderSyncModalState.form = formEl;

    modal.querySelector('#kh-profile-provider-sync-title').textContent = khMessage('profileProviderSyncTitle', 'Update provider data');
    modal.querySelector('.kh-profile-provider-sync-prompt').textContent = khMessage(
        'profileProviderSyncPrompt',
        'You changed profile data that are also stored for subscribed providers. Select the channels that should receive the update.'
    );
    modal.querySelector('[data-profile-provider-sync-skip]').textContent = khMessage('profileProviderSyncSkip', 'Save without provider update');
    modal.querySelector('[data-profile-provider-sync-apply]').textContent = khMessage('profileProviderSyncApply', 'Update selected channels');

    const fieldsContainer = modal.querySelector('.kh-profile-provider-sync-fields');
    fieldsContainer.innerHTML = '';
    if (syncMeta.changedFields.length) {
        const heading = document.createElement('strong');
        heading.textContent = khMessage('profileProviderSyncChangedFields', 'Changed fields');
        fieldsContainer.appendChild(heading);

        const list = document.createElement('ul');
        list.className = 'kh-profile-provider-sync-field-list';
        syncMeta.changedFields.forEach((field) => {
            const item = document.createElement('li');
            item.textContent = String(field.label || field.key || '');
            list.appendChild(item);
        });
        fieldsContainer.appendChild(list);
    }

    const groupsContainer = modal.querySelector('.kh-profile-provider-sync-groups');
    groupsContainer.innerHTML = '';
    const groupedTeams = new Map();

    syncMeta.teams.forEach((team) => {
        const providerName = String(team.provider_name || '').trim() || 'Provider';
        if (!groupedTeams.has(providerName)) {
            groupedTeams.set(providerName, []);
        }
        groupedTeams.get(providerName).push(team);
    });

    groupedTeams.forEach((teams, providerName) => {
        const section = document.createElement('section');
        section.className = 'kh-profile-provider-sync-group';

        const title = document.createElement('h4');
        title.textContent = providerName;
        section.appendChild(title);

        teams.forEach((team) => {
            const label = document.createElement('label');
            label.className = 'kh-profile-provider-sync-choice';

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.value = String(team.team_id || '');
            input.checked = true;
            input.setAttribute('data-profile-provider-sync-choice', '1');

            const text = document.createElement('span');
            text.textContent = String(team.team_name || '');

            label.append(input, text);
            section.appendChild(label);
        });

        groupsContainer.appendChild(section);
    });

    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const firstChoice = modal.querySelector('[data-profile-provider-sync-choice]');
    if (firstChoice) {
        firstChoice.focus();
    }
};

const khProfileCloseProviderSyncModal = () => {
    const modal = document.getElementById('kh-profile-provider-sync-modal');
    if (!modal) {
        return;
    }

    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    khProfileProviderSyncModalState.form = null;
};

jQuery(document).on('change', khAudienceLinkedSelector, function () {
    if (this.dataset.audienceLinkedSilent === '1' || !this.checked) {
        return;
    }

    const linkedGroup = khAudienceGetLinkedGroup(this);
    const uncheckedLinked = linkedGroup.filter((checkbox) => checkbox !== this && !checkbox.checked);
    if (linkedGroup.length <= 1 || uncheckedLinked.length === 0) {
        return;
    }

    khAudienceOpenLinkedModal(this, linkedGroup);
});

jQuery(document).on('change', '[data-profile-avatar-choice]', function () {
    const choice = jQuery(this);
    const editor = choice.closest('[data-profile-image-editor]');
    khProfileSetImageValue(editor, String(choice.val() || ''), String(choice.data('avatar-preview') || ''));
});

jQuery(document).on('change', '[data-profile-image-upload]', function () {
    const input = this;
    const file = input.files && input.files[0] ? input.files[0] : null;
    const editor = jQuery(input).closest('[data-profile-image-editor]');
    if (!file || !editor.length) {
        return;
    }

    const notifier = createKhNotifier({
        labels: {
            info: khMessage('noticeProfile', 'Profile'),
            success: khMessage('noticeProfile', 'Profile'),
            alert: khMessage('noticeProfile', 'Profile'),
        }
    });
    const formData = new FormData();
    formData.append('profile_image', file);
    if (khUserAccessToken) {
        formData.append('user_access_token', khUserAccessToken);
    }
    const frontendLocale = khGetFrontendLocale();
    if (frontendLocale) {
        formData.append('frontend_locale', frontendLocale);
    }

    editor.addClass('is-uploading');
    notifier.info(khMessage('profileImageUploading', 'Uploading profile picture...'), {
        durations: {
            global: 3000
        }
    });

    jQuery.ajax({
        type: 'POST',
        url: khRestUrl('user/profile/image'),
        data: formData,
        processData: false,
        contentType: false,
        dataType: 'json',
        headers: khAuthHeaders(),
        success: function (data) {
            khInvalidateRuntimeDataCaches('POST');

            const payload = getApiPayload(data);
            const imageUrl = payload && payload.url ? String(payload.url) : '';
            const imageValue = payload && payload.path ? String(payload.path) : imageUrl;
            if (!imageUrl) {
                notifier.alert(khMessage('profileImageUploadError', 'The profile picture could not be uploaded.'));
                return;
            }

            editor.find('[data-profile-avatar-choice]').prop('checked', false);
            khProfileSetImageValue(editor, imageValue, imageUrl);
            notifier.success(getApiMessage(data, khMessage('profileImageUploadSuccess', 'Profile picture uploaded and saved in your profile.')), khToastOptions({
                labels: {
                    success: khMessage('noticeProfile', 'Profile'),
                },
                durations: {
                    global: 5000
                }
            }));
        },
        error: function (xhr) {
            notifier.alert(getApiMessage(xhr.responseJSON, khMessage('profileImageUploadError', 'The profile picture could not be uploaded.')), khToastOptions({
                labels: {
                    alert: khMessage('noticeProfile', 'Profile'),
                },
                durations: {
                    global: 5000
                }
            }));
        },
        complete: function () {
            editor.removeClass('is-uploading');
            input.value = '';
        }
    });
});

jQuery(document).on('change', '[data-profile-signature-upload]', function () {
    const input = this;
    const file = input.files && input.files[0] ? input.files[0] : null;
    const editor = jQuery(input).closest('[data-profile-signature-editor]');
    if (!file || !editor.length) {
        return;
    }

    const notifier = createKhNotifier({
        labels: {
            info: khMessage('noticeProfile', 'Profile'),
            success: khMessage('noticeProfile', 'Profile'),
            alert: khMessage('noticeProfile', 'Profile'),
        }
    });
    const formData = new FormData();
    formData.append('signature_image', file);
    if (khUserAccessToken) {
        formData.append('user_access_token', khUserAccessToken);
    }
    const frontendLocale = khGetFrontendLocale();
    if (frontendLocale) {
        formData.append('frontend_locale', frontendLocale);
    }

    editor.addClass('is-uploading');
    notifier.info(khMessage('profileSignatureUploading', 'Uploading signature...'), {
        durations: {
            global: 3000
        }
    });

    jQuery.ajax({
        type: 'POST',
        url: khRestUrl('user/profile/signature'),
        data: formData,
        processData: false,
        contentType: false,
        dataType: 'json',
        headers: khAuthHeaders(),
        success: function (data) {
            khInvalidateRuntimeDataCaches('POST');

            const status = editor.find('[data-profile-signature-status]').first();
            status.addClass('is-ready');
            status.find('.material-symbols-outlined').text('verified');
            status.find('span').last().text(khMessage('profileSignatureStored', 'Signature stored'));

            notifier.success(getApiMessage(data, khMessage('profileSignatureUploadSuccess', 'Signature uploaded and saved privately in your profile.')), khToastOptions({
                labels: {
                    success: khMessage('noticeProfile', 'Profile'),
                },
                durations: {
                    global: 5000
                }
            }));
        },
        error: function (xhr) {
            notifier.alert(getApiMessage(xhr.responseJSON, khMessage('profileSignatureUploadError', 'The signature could not be uploaded.')), khToastOptions({
                labels: {
                    alert: khMessage('noticeProfile', 'Profile'),
                },
                durations: {
                    global: 5000
                }
            }));
        },
        complete: function () {
            editor.removeClass('is-uploading');
            input.value = '';
        }
    });
});

let khCertificateSignatureCanvasState = null;

const khEnsureCertificateSignatureModal = () => {
    let modal = document.getElementById('kh-certificate-signature-modal');
    if (modal) {
        return modal;
    }

    modal = document.createElement('div');
    modal.id = 'kh-certificate-signature-modal';
    modal.className = 'kh-modal kh-certificate-signature-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
        <div class="kh-modal-card kh-certificate-signature-card" role="dialog" aria-modal="true" aria-labelledby="kh-certificate-signature-title">
            <div class="kh-modal-head">
                <h3 id="kh-certificate-signature-title" class="kh-panel-title">${khMessage('certificateSignatureTitle', 'Sign certificate')}</h3>
                <button type="button" class="kh-modal-close" data-certificate-signature-close>${khMessage('close', 'Close')}</button>
            </div>
            <p class="kh-certificate-signature-copy">${khMessage('certificateSignaturePrompt', 'This certificate requires a signature before download. Upload a signature image or draw directly here.')}</p>
            <div class="kh-certificate-signature-grid">
                <section class="kh-certificate-signature-upload">
                    <h4>${khMessage('certificateSignatureUploadTitle', 'Use file')}</h4>
                    <label class="btn btn-secondary">
                        <span class="material-symbols-outlined" aria-hidden="true">upload</span>
                        ${khMessage('certificateSignatureUploadButton', 'Choose signature file')}
                        <input type="file" accept="image/jpeg,image/png,image/webp" data-certificate-signature-file>
                    </label>
                    <small data-certificate-signature-file-name>${khMessage('certificateSignatureNoFile', 'No file selected.')}</small>
                </section>
                <section class="kh-certificate-signature-draw">
                    <h4>${khMessage('certificateSignatureDrawTitle', 'Draw signature')}</h4>
                    <canvas width="640" height="220" data-certificate-signature-canvas></canvas>
                    <button type="button" class="btn btn-secondary" data-certificate-signature-clear>
                        <span class="material-symbols-outlined" aria-hidden="true">ink_eraser</span>
                        ${khMessage('certificateSignatureClear', 'Clear')}
                    </button>
                </section>
            </div>
            <label class="kh-certificate-signature-save">
                <input type="checkbox" data-certificate-signature-save>
                <span>${khMessage('certificateSignatureSave', 'Save this signature in my profile for future certificate downloads.')}</span>
            </label>
            <p class="kh-certificate-signature-error" data-certificate-signature-error hidden></p>
            <div class="kh-actions kh-certificate-signature-actions">
                <button type="button" class="btn btn-secondary" data-certificate-signature-cancel>${khMessage('cancel', 'Cancel')}</button>
                <button type="button" class="btn btn-primary" data-certificate-signature-submit>
                    <span class="material-symbols-outlined" aria-hidden="true">download</span>
                    ${khMessage('certificateSignatureDownload', 'Sign and download')}
                </button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    const closeModal = () => khCloseCertificateSignatureModal();
    modal.addEventListener('click', (event) => {
        if (event.target === modal || event.target.closest('[data-certificate-signature-close]') || event.target.closest('[data-certificate-signature-cancel]')) {
            closeModal();
        }
    });

    modal.querySelector('[data-certificate-signature-file]').addEventListener('change', (event) => {
        const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
        modal.querySelector('[data-certificate-signature-file-name]').textContent = file
            ? file.name
            : khMessage('certificateSignatureNoFile', 'No file selected.');
        khSetCertificateSignatureError('');
    });

    modal.querySelector('[data-certificate-signature-clear]').addEventListener('click', () => {
        khClearCertificateSignatureCanvas(modal);
        khSetCertificateSignatureError('');
    });

    modal.querySelector('[data-certificate-signature-submit]').addEventListener('click', () => {
        khSubmitCertificateSignatureDownload(modal);
    });

    khSetupCertificateSignatureCanvas(modal);

    return modal;
};

const khSetupCertificateSignatureCanvas = (modal) => {
    const canvas = modal.querySelector('[data-certificate-signature-canvas]');
    if (!canvas) {
        return;
    }

    const context = canvas.getContext('2d');
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = 4;
    context.strokeStyle = '#111827';

    khCertificateSignatureCanvasState = {
        isDrawing: false,
        hasInk: false,
    };

    const getPoint = (event) => {
        const rect = canvas.getBoundingClientRect();
        const source = event.touches && event.touches[0] ? event.touches[0] : event;
        return {
            x: (source.clientX - rect.left) * (canvas.width / rect.width),
            y: (source.clientY - rect.top) * (canvas.height / rect.height),
        };
    };

    const start = (event) => {
        event.preventDefault();
        const point = getPoint(event);
        khCertificateSignatureCanvasState.isDrawing = true;
        context.beginPath();
        context.moveTo(point.x, point.y);
    };

    const move = (event) => {
        if (!khCertificateSignatureCanvasState.isDrawing) {
            return;
        }
        event.preventDefault();
        const point = getPoint(event);
        context.lineTo(point.x, point.y);
        context.stroke();
        khCertificateSignatureCanvasState.hasInk = true;
    };

    const end = () => {
        khCertificateSignatureCanvasState.isDrawing = false;
    };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    canvas.addEventListener('touchstart', start, {passive: false});
    canvas.addEventListener('touchmove', move, {passive: false});
    window.addEventListener('touchend', end);
};

const khClearCertificateSignatureCanvas = (modal) => {
    const canvas = modal.querySelector('[data-certificate-signature-canvas]');
    const context = canvas ? canvas.getContext('2d') : null;
    if (!canvas || !context) {
        return;
    }
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (khCertificateSignatureCanvasState) {
        khCertificateSignatureCanvasState.hasInk = false;
    }
};

const khOpenCertificateSignatureModal = (link) => {
    const modal = khEnsureCertificateSignatureModal();
    modal.dataset.certificateDownloadUrl = link.href || '';
    modal.dataset.certificateSignatureUrl = link.getAttribute('data-certificate-signature-url') || '';
    modal.dataset.certificateId = link.getAttribute('data-certificate-id') || '';
    modal.dataset.certificateName = link.getAttribute('data-certificate-name') || '';
    modal.querySelector('[data-certificate-signature-file]').value = '';
    modal.querySelector('[data-certificate-signature-file-name]').textContent = khMessage('certificateSignatureNoFile', 'No file selected.');
    modal.querySelector('[data-certificate-signature-save]').checked = false;
    khClearCertificateSignatureCanvas(modal);
    khSetCertificateSignatureError('');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    modal.querySelector('[data-certificate-signature-file]').focus();
};

const khCloseCertificateSignatureModal = () => {
    const modal = document.getElementById('kh-certificate-signature-modal');
    if (!modal) {
        return;
    }
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
};

const khSetCertificateSignatureError = (message) => {
    const modal = document.getElementById('kh-certificate-signature-modal');
    const error = modal ? modal.querySelector('[data-certificate-signature-error]') : null;
    if (!error) {
        return;
    }
    error.textContent = message || '';
    error.hidden = !message;
};

const khCertificateDownloadFilename = (certificateName, certificateId) => {
    const safeName = String(certificateName || '')
        .replace(/[\x00-\x1F\x7F/\\:*?"<>|]+/g, '-')
        .replace(/\s+/g, ' ')
        .replace(/^[\s._-]+|[\s._-]+$/g, '');
    const baseName = safeName || ('certificate_' + (certificateId || 'signed'));
    return baseName.replace(/\.pdf$/i, '') + '.pdf';
};

const khDownloadCertificateBlob = (blob, certificateName, certificateId) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = khCertificateDownloadFilename(certificateName, certificateId);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
};

const khSubmitCertificateSignatureDownload = async (modal) => {
    const signatureUrl = modal.dataset.certificateSignatureUrl || '';
    const certificateId = modal.dataset.certificateId || '';
    const certificateName = modal.dataset.certificateName || '';
    const fileInput = modal.querySelector('[data-certificate-signature-file]');
    const canvas = modal.querySelector('[data-certificate-signature-canvas]');
    const submitButton = jQuery(modal).find('[data-certificate-signature-submit]');
    const file = fileInput && fileInput.files && fileInput.files[0] ? fileInput.files[0] : null;
    const hasInk = !!(khCertificateSignatureCanvasState && khCertificateSignatureCanvasState.hasInk);

    if (!signatureUrl) {
        khSetCertificateSignatureError(khMessage('certificateSignatureMissingUrl', 'The signed download endpoint is missing.'));
        return;
    }

    if (!file && !hasInk) {
        khSetCertificateSignatureError(khMessage('certificateSignatureRequired', 'Please upload or draw a signature.'));
        return;
    }

    const formData = new FormData();
    if (file) {
        formData.append('signature_image', file);
    } else {
        formData.append('signature_data', canvas.toDataURL('image/png'));
    }
    formData.append('save_signature', modal.querySelector('[data-certificate-signature-save]').checked ? '1' : '0');
    if (certificateName) {
        formData.append('certificate_name', certificateName);
    }
    if (khUserAccessToken) {
        formData.append('user_access_token', khUserAccessToken);
    }
    const frontendLocale = khGetFrontendLocale();
    if (frontendLocale) {
        formData.append('frontend_locale', frontendLocale);
    }

    khSetCertificateSignatureError('');
    khSetProfileButtonLoading(submitButton, true);

    try {
        const response = await fetch(signatureUrl, {
            method: 'POST',
            body: formData,
            headers: khAuthHeaders(),
            credentials: 'same-origin',
        });

        const contentType = response.headers.get('content-type') || '';
        if (!response.ok || !contentType.includes('application/pdf')) {
            let message = khMessage('certificateSignatureDownloadError', 'The signed certificate could not be downloaded.');
            try {
                const payload = await response.json();
                message = getApiMessage(payload, message);
            } catch (error) {}
            khSetCertificateSignatureError(message);
            return;
        }

        const blob = await response.blob();
        khDownloadCertificateBlob(blob, certificateName, certificateId);
        if (modal.querySelector('[data-certificate-signature-save]').checked) {
            document.querySelectorAll('[data-certificate-download]').forEach((link) => {
                link.setAttribute('data-certificate-user-has-signature', '1');
            });
        }
        khCloseCertificateSignatureModal();
    } catch (error) {
        khSetCertificateSignatureError(khMessage('certificateSignatureDownloadError', 'The signed certificate could not be downloaded.'));
    } finally {
        khSetProfileButtonLoading(submitButton, false);
    }
};

jQuery(document).on('click', 'a[data-certificate-download]', function (event) {
    const link = this;
    const requiresSignature = String(link.getAttribute('data-certificate-signature-required') || '') === '1';
    const hasStoredSignature = String(link.getAttribute('data-certificate-user-has-signature') || '') === '1';

    if (!requiresSignature || hasStoredSignature) {
        return;
    }

    event.preventDefault();
    khOpenCertificateSignatureModal(link);
});

jQuery(document).on('submit', 'form[data-profile-ajax]', function (event) {
    event.preventDefault();

    const form = jQuery(this);
    const formEl = form.get(0);
    const action = String(form.data('profile-ajax') || '');
    const path = khProfileAjaxPaths[action];
    const submitButton = form.find('button[type="submit"], button:not([type])').last();
    if (!path || submitButton.prop('disabled')) {
        return;
    }

    if ((action === 'personal' || action === 'work') && formEl && !khProfileHasProviderSyncSelection(formEl) && formEl.dataset.profileSyncConfirmed !== '1') {
        const syncMeta = khProfileCollectProviderSyncChanges(formEl);
        if (syncMeta && syncMeta.teams && syncMeta.teams.length) {
            khProfileOpenProviderSyncModal(formEl, syncMeta);
            return;
        }
    }

    const notifier = createKhNotifier({
        labels: {
            info: khMessage('noticeProfile', 'Profile'),
            success: khMessage('noticeProfile', 'Profile'),
            alert: khMessage('noticeProfile', 'Profile'),
        }
    });

    khSetProfileButtonLoading(submitButton, true);
    notifier.info(khMessage('profileSaving', 'Saving your data...'), {
        durations: {
            global: 3000
        }
    });

    khRestAjax({
        method: 'POST',
        path,
        data: khProfileFormData(form),
        success: function (data) {
            const message = getApiMessage(data, khMessage('profileSaveSuccess', 'Your update was successful.'));
            notifier.success(message, khToastOptions({
                labels: {
                    success: khMessage('noticeProfile', 'Profile'),
                },
                durations: {
                    global: 5000
                }
            }));

            const payload = getApiPayload(data);
            const redirectUrl = payload && payload.redirect ? payload.redirect : khProfileViewUrl();
            if (payload && payload.redirect) {
                window.setTimeout(() => {
                    window.location.href = redirectUrl;
                }, 3000);
                return;
            }

            window.setTimeout(() => {
                window.location.href = redirectUrl;
            }, 1500);
        },
        error: function (xhr) {
            const message = getApiMessage(xhr.responseJSON, khMessage('profileSaveError', 'Something went wrong while updating your data. Please try again.'));
            notifier.alert(message, khToastOptions({
                labels: {
                    alert: khMessage('noticeProfile', 'Profile'),
                },
                durations: {
                    global: 5000
                }
            }));
        },
        complete: function () {
            khSetProfileButtonLoading(submitButton, false);
            if (formEl) {
                delete formEl.dataset.profileSyncConfirmed;
            }
        }
    });
});

const channelWaitlistMessage = khMessage('channelWaitlist', 'You are on the waiting list. An admin must approve your request.');

const isChannelWaitlistResponse = (data) => {
    const payload = getApiPayload(data);

    if (!payload || typeof payload !== 'object') {
        return false;
    }

    const waitlistKeys = [
        'waitlist',
        'waitlisted',
        'waiting',
        'waiting_list',
        'in_waitlist',
        'in_waiting_list',
        'on_waiting_list',
        'is_waitlisted',
        'is_waiting_list',
        'pending',
        'is_pending',
        'pending_approval',
        'is_pending_approval',
        'requires_approval',
        'approval_required',
        'admin_approval_required',
        'waitingList',
        'inWaitlist',
        'inWaitingList',
        'onWaitingList',
        'isWaitlisted',
        'isWaitingList',
        'isPending',
        'pendingApproval',
        'isPendingApproval',
        'requiresApproval',
        'approvalRequired',
        'adminApprovalRequired',
    ];
    const isTruthy = (value) => value === true || value === 1 || ['1', 'true', 'yes', 'on'].includes(String(value || '').trim().toLowerCase());
    const isFalsey = (value) => value === false || value === 0 || ['0', 'false', 'no', 'off'].includes(String(value || '').trim().toLowerCase());

    for (const key of waitlistKeys) {
        if (isTruthy(payload[key])) {
            return true;
        }
    }

    const status = String(
        payload.status ||
        payload.subscription_status ||
        payload.subscription_state ||
        payload.membership_status ||
        payload.membership_state ||
        payload.join_status ||
        payload.join_state ||
        payload.approval_status ||
        payload.approval_state ||
        payload.membershipStatus ||
        payload.membershipState ||
        payload.joinStatus ||
        payload.joinState ||
        payload.subscriptionStatus ||
        payload.subscriptionState ||
        payload.approvalStatus ||
        payload.approvalState ||
        payload.requestStatus ||
        payload.memberStatus ||
        payload.request_status ||
        payload.member_status ||
        payload.state ||
        ''
    ).toLowerCase();

    if (/(warteliste|waitlist|waiting|pending|approval|request)/.test(status)) {
        return true;
    }

    if (
        (Object.prototype.hasOwnProperty.call(payload, 'access_granted') && isFalsey(payload.access_granted))
        || (Object.prototype.hasOwnProperty.call(payload, 'accessGranted') && isFalsey(payload.accessGranted))
    ) {
        return true;
    }

    const message = String(
        payload.message ||
        payload.status_message ||
        payload.statusMessage ||
        payload.notice ||
        payload.detail ||
        ''
    ).toLowerCase();
    return /(warteliste|waiting list|waitlist|pending|approval|anfrage.*bestät|admin.*bestät)/.test(message);
};

function khReconcileLeftChannelCache(teams) {
    const activeTeamIds = new Set((Array.isArray(teams) ? teams : [])
        .filter((team) => team && typeof team === 'object' && !isChannelWaitlistResponse(team))
        .map((team) => team?.id || team?.team_id || team?.teamId || '')
        .map(khNormalizeChannelId)
        .filter(Boolean));

    if (!activeTeamIds.size) {
        return false;
    }

    const storedIds = khReadLeftChannelIds();
    const nextIds = storedIds.filter((teamId) => !activeTeamIds.has(teamId));

    if (nextIds.length === storedIds.length) {
        return false;
    }

    khWriteLeftChannelIds(nextIds);
    return true;
}

async function khRefreshLeftChannelCache() {
    if (!khUserAccessToken || !window.fetch || !khReadLeftChannelIds().length) {
        return null;
    }

    let profile = null;
    if (window.khSessionWarmupPromise && typeof window.khSessionWarmupPromise.then === 'function') {
        profile = await window.khSessionWarmupPromise.catch(() => null);
    }

    if (!Array.isArray(profile?.teams)) {
        profile = await khUserRestClient.requestJson('user/profile');
    }

    if (Array.isArray(profile?.teams)) {
        khReconcileLeftChannelCache(profile.teams);
    }

    return profile;
}

window.khRefreshLeftChannelCache = khRefreshLeftChannelCache;
window.khReconcileLeftChannelCache = khReconcileLeftChannelCache;

const getChannelSubscribeSuccessMessage = (data) => {
    if (isChannelWaitlistResponse(data)) {
        return channelWaitlistMessage;
    }

    return getApiMessage(data, khMessage('channelSubscribeSuccess', 'Channel subscribed successfully.'));
};

const getChannelNameFromSubscribeContext = (button, data) => {
    const buttonName = String(button.data('team-name') || '').trim();
    if (buttonName) {
        return buttonName;
    }

    const payload = getApiPayload(data);
    if (payload && typeof payload === 'object') {
        const candidates = [
            payload.team_name,
            payload.channel_name,
            payload.name,
            payload.team?.team_name,
            payload.team?.name,
            payload.team_data?.team_name,
            payload.team_data?.name,
            payload.membership?.team_name,
            payload.membership?.name,
        ];

        for (const candidate of candidates) {
            const value = String(candidate || '').trim();
            if (value) {
                return value;
            }
        }
    }

    const heading = button.closest('.t-content-head, .t-content-element, .t-profile-recommended-card, article, section')
        .find('h1, h2, h3, .t-channel-search-title')
        .first()
        .text();
    return String(heading || '').trim();
};

const storeChannelSubscribeNotification = (button, teamId, data) => {
    const teamName = getChannelNameFromSubscribeContext(button, data);
    const isWaitlist = isChannelWaitlistResponse(data);

    khStoreLocalNotification({
        id: (isWaitlist ? 'channel-waitlist:' : 'channel-subscribed:') + String(teamId) + ':' + Date.now(),
        icon: isWaitlist ? 'pending_actions' : 'groups',
        title: isWaitlist
            ? khMessage('channelWaitlistNotificationTitle', 'Channel waiting list')
            : khMessage('channelSubscribeNotificationTitle', 'Channel subscribed'),
        message: (isWaitlist
            ? khMessage('channelWaitlistNotificationMessage', 'You are on the waiting list for "%s". An admin must approve your request.')
            : khMessage('channelSubscribeNotificationMessage', 'You have subscribed to the channel "%s".'))
            .replace('%s', teamName || khMessage('noticeChannel', 'Channel')),
        url: window.location.href,
    });
};

jQuery(document).on("click", "button.mark-content-for-user", function () {
    let content_id = jQuery(this).data('content-id');
    let globalOptions = {
        labels: {
            success: khMessage('noticeWatchlist', 'Watchlist'),
        }
    };
    let notifier = createKhNotifier(globalOptions);
    notifier.success(khMessage('contentMarking', 'Content is being marked...'), {

        durations: {
            global: 5000
        }
    });
    khRestAjax({
        method: 'POST',
        path: 'user/marks/' + encodeURIComponent(content_id),
        success: function (data) {
            jQuery('#mark-toggle-span-' + content_id + ' > span').empty().append(
                khMarkContentLabel('marked', 'You have marked this content')
            );

            jQuery('#mark-toggle-' + content_id).addClass('marked');
            jQuery('#btn-mark-' + content_id)
                .attr('aria-label', khMarkContentLabel('marked', 'You have marked this content'))
                .attr('title', khMarkContentLabel('marked', 'You have marked this content'));
            // alter button
            jQuery('#btn-mark-' + content_id).removeClass('t-mark-content');
            jQuery('#btn-mark-' + content_id).removeClass('mark-content-for-user');
            jQuery('#btn-mark-' + content_id).addClass('t-marked-content');
            jQuery('#btn-mark-' + content_id).addClass('mark-remove-content-for-user');
        },
        error: function (errorThrown) {
        }
    });
});

jQuery(document).on("click", "button.mark-remove-content-for-user", function () {
    let content_id = jQuery(this).data('content-id');
    let globalOptions = {
        labels: {
            success: khMessage('noticeWatchlist', 'Watchlist'),
        }
    };
    let notifier = createKhNotifier(globalOptions);

    notifier.success(khMessage('contentRemovingFromWatchlist', 'Content is being removed from the watchlist.'), {

        durations: {
            global: 5000
        }
    });
    khRestAjax({
        method: 'DELETE',
        path: 'user/marks/' + encodeURIComponent(content_id),
        success: function (data) {


            jQuery('#dir-' + content_id).fadeOut();
            jQuery('#mark-toggle-span-' + content_id + ' > span').empty().append(
                khMarkContentLabel('unmarked', 'Mark this content')
            );
            jQuery('#mark-toggle-' + content_id).removeClass('marked');
            jQuery('#btn-mark-' + content_id)
                .attr('aria-label', khMarkContentLabel('unmarked', 'Mark this content'))
                .attr('title', khMarkContentLabel('unmarked', 'Mark this content'));
            // alter button
            jQuery('#btn-mark-' + content_id).removeClass('t-marked-content');
            jQuery('#btn-mark-' + content_id).removeClass('mark-remove-content-for-user');
            jQuery('#btn-mark-' + content_id).addClass('t-mark-content');
            jQuery('#btn-mark-' + content_id).addClass('mark-content-for-user');

        },
        error: function (errorThrown) {
        }
    });
});

jQuery(document).on("click", "div.accordion-item", function (event) {
    if (event.target.closest('.kh-content-certificate-link')) {
        return;
    }
    const $item = jQuery(this);
    if ($item.hasClass('kh-content-lazy-item') && this.dataset.khContentableLoaded !== '1') {
        return;
    }

    const contentType = String($item.data('content-type-id') || '').toLowerCase();
    const isTestContent = $item.hasClass('content-type-test') || $item.hasClass('content-type-test-attachment') || contentType === 'test';

    if (isTestContent) {
        return;
    }

    if (khCanTrackWithConsent()) {
        khTrackContentAnalytics($item.data('content-id'), $item.data('content-type-id'), {
            progress: 100,
        }, {
            success: function (data) {
                setContentCompletionState($item[0], true);
                refreshContentCertificateContext($item[0]);
            },
            error: function (errorThrown) {

            }
        });
    }
});

jQuery(document).on('click', '.t-content-attachment-download-link', function (event) {
    event.preventDefault();
    event.stopPropagation();

    const $link = jQuery(this);

    if (khCanTrackWithConsent()) {
        khTrackContentAnalytics($link.data('content-id'), $link.data('content-type-id'), {
            download_triggered: true,
        });
    }

    khDownloadAttachment($link).catch(() => {});
});


jQuery(document).on("click", "button.t-event-register", function (e) {
    e.preventDefault();
    let event_id = jQuery(this).data('event-id');
    // Formvalidierung - alle required Felder im nächsten Form-Element finden
    let form = jQuery('#event-form-' + event_id);
    let requiredFields = form.find('[required]');
    let isValid = true;
    let emptyFields = [];

    // Überprüfe jedes required Feld, Checkboxen werden speziell behandelt
    requiredFields.each(function () {
        let field = jQuery(this);
        let type = field.attr('type');

        if (type === 'checkbox') {
            if (!field.is(':checked')) {
                isValid = false;
                field.addClass('is-invalid');
            } else {
                field.removeClass('is-invalid');
            }
        } else {
            let value = field.val().trim();
            if (value === '' || value === null) {
                isValid = false;
                field.addClass('is-invalid');
            } else {
                field.removeClass('is-invalid');
            }
        }
    });

    // Wenn nicht alle Felder ausgefüllt sind, zeige Errormeldung
    if (!isValid) {
        let globalOptions = {
            labels: {
                alert: khMessage('input', 'Input')
            }
        };
        let notifier = createKhNotifier(globalOptions);
        notifier.alert(
            khMessage('eventRequiredFields', 'Please fill in all required fields: %s').replace('%s', emptyFields.join(', ')),
            {

            durations: {
                global: 3000
            }
            }
        );
        return;
    } else {

        let required_data = form.serializeArray();

        //let event_id = jQuery(this).data('event-id');
        let button = jQuery(this);
        let globalOptions = {
            labels: {
                tip: khMessage('noticeRegistration', 'Registration'),
                success: khMessage('noticeRegistration', 'Registration')
            }
        };
        let notifier = createKhNotifier(globalOptions);
        notifier.tip(khMessage('eventRegistering', 'You are being registered...'), {

            durations: {
                global: 5000
            }
        });
        khRestAjax({
            method: 'POST',
            path: 'event/' + encodeURIComponent(jQuery(this).data('event-id')) + '/accept',
            data: {
                user_details: JSON.stringify(required_data),
            },
            success: function (data) {
                notifier.success(khMessage('eventRegisterSuccess', 'You have successfully registered for the event.'), {

                    durations: {
                        global: 5000
                    }
                });
                jQuery('#t-event-status-icon').replaceWith(
                    '<div id="t-event-status-icon"><div class="summary-success"><span class="t-nav-list-element-icon material-symbols-outlined">how_to_reg</span></div></div>'
                );
                jQuery('#t-event-user-status-message').css('display', 'flex');
                button.removeClass('t-event-register btn-primary')
                    .addClass('t-event-unregister btn-secondary')
                    .attr('name', 't-event-unregister')
                    .text(khMessage('eventUnregisterButton', 'Unregister from event'));
                khReloadWithRefreshData();
            },
            error: function (errorThrown) {

            }
        });
    }
});

jQuery(document).on("click", "button.t-event-unregister", function (e) {
    let event_id = jQuery(this).data('event-id');
    let button = jQuery(this);
    let globalOptions = {
        labels: {
            tip: khMessage('eventUnregisteringTitle', 'Unsubscribing'),
            success: khMessage('eventUnregisteringTitle', 'Unsubscribing')
        }
    };
    let notifier = createKhNotifier(globalOptions);
    notifier.tip(khMessage('eventUnregistering', 'You are being unregistered...'), {
        title: khMessage('eventUnregisteringTitle', 'Unsubscribing'),

        durations: {
            global: 5000
        }
    });
    khRestAjax({
        method: 'POST',
        path: 'event/' + encodeURIComponent(event_id) + '/decline',
        success: function (data) {
            notifier.success(khMessage('eventUnregisterSuccess', 'You have successfully unregistered from the event.'), {

                durations: {
                    global: 5000
                }
            });

            jQuery('#t-event-status-icon').replaceWith(
                '<div id="t-event-status-icon"><div class="summary-icon"><span class="t-nav-list-element-icon material-symbols-outlined">event</span></div></div>'
            );
            jQuery('#t-event-user-status-message').css('display', 'none');
            button.removeClass('t-event-unregister btn-secondary')
                .addClass('t-event-register btn-primary')
                .attr('name', 't-event-register')
                .text(khMessage('eventRegisterButton', 'Register for event'));
            khReloadWithRefreshData();
        },
        error: function (errorThrown) {

        }
    });
});

jQuery(document).ready(function ($) {
    var btn = $('#scrollToTopBtn');

    $(window).scroll(function () {
        if ($(window).scrollTop() > 300) {
            btn.fadeIn();
        } else {
            btn.fadeOut();
        }
    });

    btn.on('click', function (e) {
        e.preventDefault();
        $('html, body').animate({scrollTop: 0}, '300');
    });
});

jQuery(document)
    .off('click.khLegacyAccordionScroll', 'summary')
    .on('click.khLegacyAccordionScroll', 'summary', function () {
    var accordionItem = jQuery(this).closest('.accordion-item');
    var summary = this;
    var details = summary.closest('details');

    setTimeout(function () {
        if (details && details.open) {
            var target = summary || accordionItem.find('summary.accordion-header').get(0) || accordionItem.get(0);
            if (typeof window.khScrollElementBelowTopbar === 'function') {
                window.khScrollElementBelowTopbar(target);
                return;
            }

            var offsetTop = jQuery(target).offset().top;
            var headerOffset = typeof window.khGetFixedTopOffset === 'function' ? window.khGetFixedTopOffset() : 120;
            window.scrollTo({
                top: Math.max(offsetTop - headerOffset, 0),
                behavior: 'smooth'
            });
        }
    }, 200);
});

let channelSubscribeModal = null;
let channelSubscribeModalCheckbox = null;
let channelSubscribeModalError = null;
let channelSubscribeModalConfirm = null;
let channelSubscribeModalAdditionalForm = null;
let channelSubscribeModalIntro = null;
let channelSubscribeModalConsentCopy = null;
let channelSubscribeModalPrivacyLink = null;
let pendingSubscribeButton = null;
let pendingSubscribeTeamId = null;
let pendingSubscribeJoinedTeamId = null;
let pendingSubscribeRedirectTeamId = null;
let pendingSubscribeAdditionalFields = [];

const khEscapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const khNormalizePrivacyPolicyUrl = (value) => {
    const rawUrl = String(value || '').trim();
    if (!rawUrl) {
        return '';
    }

    try {
        const parsedUrl = new URL(rawUrl, window.location.origin);
        if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
            return '';
        }

        return parsedUrl.href;
    } catch (error) {
        return '';
    }
};

const khDefaultPrivacyPolicyUrl = () => khNormalizePrivacyPolicyUrl(
    'https://teejit.io/datenschutz/'
);

const khExtractPrivacyPolicyUrlFromPayload = (data) => {
    const payload = getApiPayload(data);
    if (!payload || typeof payload !== 'object') {
        return '';
    }

    const candidates = [
        payload.privacy_policy_url,
        payload.provider_privacy_policy_url,
        payload.provider_privacy_url,
        payload.privacy_url,
        payload.primary_provider?.privacy_policy_url,
        payload.primary_provider?.privacy_url,
        payload.provider?.privacy_policy_url,
        payload.provider?.privacy_url,
        payload.team?.privacy_policy_url,
        payload.team?.privacy_url,
        payload.team_data?.privacy_policy_url,
        payload.team_data?.privacy_url,
        payload.membership?.privacy_policy_url,
        payload.membership?.privacy_url,
    ];

    for (const candidate of candidates) {
        const privacyUrl = khNormalizePrivacyPolicyUrl(candidate);
        if (privacyUrl) {
            return privacyUrl;
        }
    }

    const providers = Array.isArray(payload.providers) ? payload.providers : [];
    for (const provider of providers) {
        if (!provider || typeof provider !== 'object') {
            continue;
        }

        const providerUrl = khNormalizePrivacyPolicyUrl(provider.privacy_policy_url || provider.privacy_url);
        if (providerUrl) {
            return providerUrl;
        }
    }

    return '';
};

const khGetChannelPrivacyPolicyUrl = (teamId, button = null, data = null) => {
    const payloadPrivacyUrl = khExtractPrivacyPolicyUrlFromPayload(data);
    if (payloadPrivacyUrl) {
        return payloadPrivacyUrl;
    }

    const buttonPrivacyUrl = button
        ? khNormalizePrivacyPolicyUrl(button.data('provider-privacy-url') || button.attr('data-provider-privacy-url'))
        : '';
    if (buttonPrivacyUrl) {
        return buttonPrivacyUrl;
    }

    const normalizedTeamId = teamId ? String(teamId) : '';
    const privacyUrlMap = window.khChannelPrivacyPolicyUrlsByTeam || {};
    if (normalizedTeamId && privacyUrlMap[normalizedTeamId]) {
        return khNormalizePrivacyPolicyUrl(privacyUrlMap[normalizedTeamId]);
    }

    return khDefaultPrivacyPolicyUrl();
};

const khPrivacyPolicyLinkHtml = (privacyUrl) => {
    const normalizedPrivacyUrl = khNormalizePrivacyPolicyUrl(privacyUrl);
    if (!normalizedPrivacyUrl) {
        return '';
    }

    return '<a class="channel-subscribe-privacy-link" href="' + khEscapeHtml(normalizedPrivacyUrl) + '" target="_blank" rel="noopener noreferrer">' +
        khEscapeHtml(khMessage('channelSubscribePrivacyLinkLabel', 'Privacy Policy')) +
        '</a>';
};

const khUpdateChannelSubscribePrivacyCopy = (privacyUrl) => {
    if (channelSubscribeModalIntro) {
        channelSubscribeModalIntro.html(khEscapeHtml(khMessage('channelSubscribeIntro', "Please confirm before subscribing that your user information may be transferred to the channel provider and that you accept the provider's privacy policy.")));
    }

    if (!channelSubscribeModalConsentCopy) {
        return;
    }

    const linkHtml = khPrivacyPolicyLinkHtml(privacyUrl);
    channelSubscribeModalConsentCopy.html(khEscapeHtml(khMessage('channelSubscribeConsent', "I confirm that my user information will be shared with the channel provider when I subscribe, and that I accept the provider's privacy policy.")));

    if (channelSubscribeModalPrivacyLink) {
        channelSubscribeModalPrivacyLink.html(linkHtml).toggle(!!linkHtml);
    }
};

const getChannelAdditionalFieldKey = (field, fallback = '') => String(
    field?.field_key ?? field?.name ?? field?.key ?? field?.id ?? fallback ?? ''
).trim();

const normalizeChannelAdditionalOptions = (options) => {
    if (typeof options === 'string') {
        const raw = options.trim();
        if (!raw) {
            return [];
        }

        if (/^\s*[\[{]/.test(raw)) {
            try {
                const decoded = JSON.parse(raw);
                return normalizeChannelAdditionalOptions(decoded);
            } catch (error) {
                // Fall back to delimiter splitting below.
            }
        }

        options = raw.split(/\r\n|\r|\n|\\r\\n|\\r|\\n|[|;,]+/).map((option) => option.trim()).filter(Boolean);
    }

    if (!options || typeof options !== 'object') {
        return [];
    }

    return Object.entries(options)
        .map(([optionKey, option]) => {
            if (option && typeof option === 'object') {
                const value = String(option.value ?? optionKey ?? '');
                return {
                    value,
                    label: String(option.label ?? option.name ?? value),
                };
            }

            const scalar = String(option ?? '');
            const value = /^[0-9]+$/.test(String(optionKey)) ? scalar : String(optionKey);
            return {
                value,
                label: scalar || value,
            };
        })
        .filter((option) => option.value !== '' || option.label !== '');
};

const normalizeChannelAdditionalFields = (fields) => {
    if (!fields || typeof fields !== 'object') {
        return [];
    }

    const seen = new Set();

    return Object.entries(fields)
        .map(([fallbackKey, field]) => {
            if (!field || typeof field !== 'object') {
                return null;
            }

            const fieldKey = getChannelAdditionalFieldKey(field, fallbackKey);
            if (!fieldKey || seen.has(fieldKey)) {
                return null;
            }
            seen.add(fieldKey);

            let type = String(field.type ?? field.field_type ?? field.input_type ?? 'text').toLowerCase();
            if (type === 'dropdown') {
                type = 'select';
            } else if (type === 'phone') {
                type = 'tel';
            }

            const label = String(field.label ?? field.title ?? field.name ?? fieldKey);
            return {
                field_key: fieldKey,
                label,
                type,
                placeholder: String(field.placeholder || ''),
                help_text: String(field.help_text ?? field.help ?? field.description ?? field.info_text ?? ''),
                required: field.required === true || field.is_required === true || field.required === 1 || field.is_required === 1 || field.required === '1' || field.is_required === '1' || field.required === 'true' || field.is_required === 'true' || field.required === 'required' || field.registration_mode === 'required' || field.validation_type === 'required',
                options: normalizeChannelAdditionalOptions(field.options ?? field.values ?? field.choices ?? []),
            };
        })
        .filter(Boolean);
};

const getChannelAdditionalFieldsForTeam = (teamId) => {
    const fieldMap = window.khChannelAdditionalFormFieldsByTeam || {};
    return normalizeChannelAdditionalFields(fieldMap[String(teamId)] || []);
};

const getChannelAdditionalFieldsFromPayload = (data) => {
    const payload = getApiPayload(data);
    if (!payload || typeof payload !== 'object') {
        return [];
    }

    const fieldSources = [
        payload.additional_form_fields,
        payload.registration_fields,
        payload.feature_registration_fields,
        payload.team_additional_form_fields,
        payload.membership_additional_form_fields,
        payload.team?.additional_form_fields,
        payload.team?.registration_fields,
        payload.team?.feature_registration_fields,
        payload.team_data?.additional_form_fields,
        payload.team_data?.registration_fields,
        payload.team_data?.feature_registration_fields,
        payload.membership?.additional_form_fields,
        payload.membership?.registration_fields,
    ];

    for (const fieldSource of fieldSources) {
        const fields = normalizeChannelAdditionalFields(fieldSource);
        if (fields.length > 0) {
            return fields;
        }
    }

    return [];
};

const getChannelAdditionalTeamIdFromPayload = (data) => {
    const payload = getApiPayload(data);
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const candidates = [
        payload.team_id,
        payload.id,
        payload.team?.team_id,
        payload.team?.id,
        payload.team_data?.team_id,
        payload.team_data?.id,
        payload.membership?.team_id,
    ];

    for (const candidate of candidates) {
        const teamId = parseInt(candidate, 10);
        if (Number.isFinite(teamId) && teamId > 0) {
            return teamId;
        }
    }

    return null;
};

const renderChannelAdditionalField = (field) => {
    const key = khEscapeHtml(field.field_key);
    const label = khEscapeHtml(field.label || field.field_key);
    const placeholder = khEscapeHtml(String(field.placeholder || '').trim());
    const inputPlaceholder = placeholder || label;
    const helpText = khEscapeHtml(field.help_text || '');
    const required = field.required ? ' required="required"' : '';
    const requiredMark = field.required ? ' <span aria-hidden="true">*</span>' : '';
    const inputName = 'channel_additional_form_data[' + key + ']';
    const fieldId = 'channel-subscribe-additional-' + key.replace(/[^a-z0-9_-]/gi, '-');
    const inputType = ['date', 'email', 'number', 'tel'].includes(field.type) ? field.type : 'text';

    let control = '';
    if (field.type === 'select') {
        const options = field.options.map((option) => (
            '<option value="' + khEscapeHtml(option.value) + '">' + khEscapeHtml(option.label || option.value) + '</option>'
        )).join('');
        control = '<select id="' + fieldId + '" name="' + inputName + '" data-channel-additional-field="' + key + '"' + required + '>' +
            '<option value="">' + (placeholder || khEscapeHtml(khMessage('pleaseSelect', 'Please select'))) + '</option>' +
            options +
            '</select>';
    } else if (field.type === 'checkbox') {
        control = '<label class="channel-subscribe-additional-checkbox" for="' + fieldId + '">' +
            '<input type="checkbox" id="' + fieldId + '" name="' + inputName + '" value="1" data-channel-additional-field="' + key + '"' + required + '>' +
            '<span>' + (helpText || label) + '</span>' +
            '</label>';
    } else if (field.type === 'textarea') {
        control = '<textarea id="' + fieldId + '" name="' + inputName + '" placeholder="' + inputPlaceholder + '" data-channel-additional-field="' + key + '"' + required + '></textarea>';
    } else {
        control = '<input type="' + inputType + '" id="' + fieldId + '" name="' + inputName + '" placeholder="' + inputPlaceholder + '" data-channel-additional-field="' + key + '"' + required + '>';
    }

    return '<div class="channel-subscribe-additional-field">' +
        (field.type === 'checkbox' ? '' : '<label for="' + fieldId + '">' + label + requiredMark + '</label>') +
        control +
        (helpText && field.type !== 'checkbox' ? '<small>' + helpText + '</small>' : '') +
        '</div>';
};

const renderChannelSubscribeAdditionalFields = (fields) => {
    if (!channelSubscribeModalAdditionalForm) {
        return;
    }

    const normalizedFields = normalizeChannelAdditionalFields(fields);
    pendingSubscribeAdditionalFields = normalizedFields;

    if (normalizedFields.length === 0) {
        channelSubscribeModalAdditionalForm.empty().attr('hidden', 'hidden');
        return;
    }

    const html = '<div class="channel-subscribe-additional-title">' +
        khEscapeHtml(khMessage('channelAdditionalFormTitle', 'Additional channel information')) +
        '</div>' +
        normalizedFields.map(renderChannelAdditionalField).join('');

    channelSubscribeModalAdditionalForm.html(html).removeAttr('hidden');
};

const collectChannelSubscribeAdditionalFormData = () => {
    const data = {};
    let missingLabel = '';

    pendingSubscribeAdditionalFields.forEach((field) => {
        const key = field.field_key;
        const input = channelSubscribeModalAdditionalForm
            ? channelSubscribeModalAdditionalForm.find('[data-channel-additional-field]').filter(function () {
                return jQuery(this).attr('data-channel-additional-field') === key;
            }).first()
            : jQuery();

        if (field.type === 'checkbox') {
            const checked = input.is(':checked');
            data[key] = checked ? '1' : '0';
            if (field.required && !checked && !missingLabel) {
                missingLabel = field.label || key;
            }
            return;
        }

        const value = String(input.val() ?? '').trim();
        data[key] = value;
        if (field.required && value === '' && !missingLabel) {
            missingLabel = field.label || key;
        }
    });

    if (missingLabel) {
        return {
            valid: false,
            data,
            message: khMessage('channelAdditionalFormRequired', 'Please fill in all required channel information: %s').replace('%s', missingLabel),
        };
    }

    return {
        valid: true,
        data,
        message: '',
    };
};

function getChannelSubscribeNotifier() {
    return createKhNotifier({
        labels: {
            info: khMessage('noticeChannel', 'Channel'),
            success: khMessage('noticeChannel', 'Channel'),
            warning: khMessage('noticeChannel', 'Channel'),
            alert: khMessage('noticeChannel', 'Channel')
        }
    });
}

function buildChannelSubscribeModal() {
    if (channelSubscribeModal) {
        return;
    }

    const modalHtml = `
        <div id="channel-subscribe-modal" class="channel-subscribe-overlay" style="display:none;">
            <div class="channel-subscribe-dialog" role="dialog" aria-modal="true" aria-labelledby="channel-subscribe-title">
                <div class="channel-subscribe-header">
                    <h5 id="channel-subscribe-title" class="channel-subscribe-title">${khMessage('channelSubscribeTitle', 'Subscribe to channel')}</h5>
                    <button type="button" class="channel-subscribe-close" aria-label="${khMessage('close', 'Close')}"><span class="material-symbols-outlined">close</span></button>
                </div>
                <div class="channel-subscribe-body">
                    <p class="channel-subscribe-text" data-channel-subscribe-intro></p>
                    <div class="channel-user-data-consent-wrap">
                        <input
                            type="checkbox"
                            name="channel_subscribe_modal_user_data_consent"
                            id="channel-subscribe-user-data-consent"
                            class="channel-user-data-consent checkbox-icon"
                            value="1"
                            required="required"
                        />
                        <label for="channel-subscribe-user-data-consent" class="checkbox-icon-label">
                            <div class="checkbox-icon checkbox-icon-unchecked"><span class="material-symbols-outlined">check_box_outline_blank</span></div>
                            <div class="checkbox-icon checkbox-icon-checked"><span class="material-symbols-outlined">check_box</span></div>
                            <p data-channel-subscribe-consent-copy></p>
                        </label>
                        <p class="channel-subscribe-privacy-copy" data-channel-subscribe-privacy-link style="display:none;"></p>
                    </div>
                    <div id="channel-subscribe-additional-form" class="channel-subscribe-additional-form" hidden></div>
                    <div id="channel-subscribe-error" class="channel-subscribe-error" style="display:none;"></div>
                </div>
                <div class="channel-subscribe-footer">
                    <button type="button" class="btn btn-secondary channel-subscribe-close">${khMessage('cancel', 'Cancel')}</button>
                    <button type="button" id="channel-subscribe-confirm" class="btn btn-primary">${khMessage('channelSubscribeButton', 'Subscribe to this channel')}</button>
                </div>
            </div>
        </div>
    `;

    jQuery('body').append(modalHtml);

    channelSubscribeModal = jQuery('#channel-subscribe-modal');
    channelSubscribeModalCheckbox = jQuery('#channel-subscribe-user-data-consent');
    channelSubscribeModalError = jQuery('#channel-subscribe-error');
    channelSubscribeModalConfirm = jQuery('#channel-subscribe-confirm');
    channelSubscribeModalAdditionalForm = jQuery('#channel-subscribe-additional-form');
    channelSubscribeModalIntro = channelSubscribeModal.find('[data-channel-subscribe-intro]');
    channelSubscribeModalConsentCopy = channelSubscribeModal.find('[data-channel-subscribe-consent-copy]');
    channelSubscribeModalPrivacyLink = channelSubscribeModal.find('[data-channel-subscribe-privacy-link]');
    khUpdateChannelSubscribePrivacyCopy(khGetChannelPrivacyPolicyUrl(null));
}

function setChannelSubscribeError(message) {
    if (!channelSubscribeModalError) {
        return;
    }

    channelSubscribeModalError.text(message || '');
    channelSubscribeModalError.toggle(!!message);
}

function closeChannelSubscribeModal() {
    if (!channelSubscribeModal) {
        return;
    }

    channelSubscribeModal.hide();
    channelSubscribeModalCheckbox.prop('checked', false).removeClass('is-invalid');
    renderChannelSubscribeAdditionalFields([]);
    khUpdateChannelSubscribePrivacyCopy(khGetChannelPrivacyPolicyUrl(null));
    setChannelSubscribeError('');
    channelSubscribeModalConfirm
        .prop('disabled', false)
        .text(khMessage('channelSubscribeButton', 'Subscribe to this channel'));
    pendingSubscribeButton = null;
    pendingSubscribeTeamId = null;
    pendingSubscribeJoinedTeamId = null;
    pendingSubscribeRedirectTeamId = null;
}

function openChannelSubscribeModal(button, teamId) {
    buildChannelSubscribeModal();
    pendingSubscribeButton = button;
    pendingSubscribeTeamId = teamId;
    pendingSubscribeJoinedTeamId = null;
    pendingSubscribeRedirectTeamId = null;
    renderChannelSubscribeAdditionalFields(getChannelAdditionalFieldsForTeam(teamId));
    khUpdateChannelSubscribePrivacyCopy(khGetChannelPrivacyPolicyUrl(teamId, button));
    channelSubscribeModal.css('display', 'flex');
    channelSubscribeModalCheckbox.prop('checked', false).removeClass('is-invalid').trigger('focus');
    channelSubscribeModalConfirm.text(khMessage('channelSubscribeButton', 'Subscribe to this channel'));
    setChannelSubscribeError('');
}

function saveChannelSubscribeAdditionalFormData(button, teamId, additionalFormData = {}) {
    const notifier = getChannelSubscribeNotifier();
    const redirectTeamId = pendingSubscribeRedirectTeamId;

    button.prop('disabled', true);
    notifier.warning(khMessage('channelDataSaving', 'Saving channel data...'), {
        durations: {
            global: 5000
        }
    });

    khRestAjax({
        method: 'POST',
        path: 'team/' + encodeURIComponent(teamId) + '/additional-form-data',
        data: {
            additional_form_data: additionalFormData,
        },
        success: function (data) {
            if (!isApiSuccess(data)) {
                notifier.alert(getApiMessage(data, khMessage('channelDataCouldNotSave', 'Channel data could not be saved.')), {
                    durations: {
                        global: 5000
                    }
                });
                button.prop('disabled', false);
                channelSubscribeModalConfirm.prop('disabled', false);
                return;
            }

            closeChannelSubscribeModal();

            notifier.success(getApiMessage(data, khMessage('channelDataSaved', 'Channel data saved.')), {
                durations: {
                    global: 5000
                }
            });

            khFinishChannelJoin(redirectTeamId, 1500);
        },
        error: function () {
            notifier.alert(khMessage('channelDataCouldNotSave', 'Channel data could not be saved.'), {
                durations: {
                    global: 5000
                }
            });
            button.prop('disabled', false);
            channelSubscribeModalConfirm.prop('disabled', false);
        }
    });
}

function submitChannelSubscription(button, teamId, additionalFormData = {}) {
    const notifier = getChannelSubscribeNotifier();

    if (button.prop('disabled')) {
        return;
    }

    if (pendingSubscribeJoinedTeamId) {
        saveChannelSubscribeAdditionalFormData(button, pendingSubscribeJoinedTeamId, additionalFormData);
        return;
    }

    button.prop('disabled', true);
    notifier.warning(khMessage('channelSubscribing', 'Subscribing to channel...'), {
        durations: {
            global: 5000
        }
    });

    khRestAjax({
        method: 'POST',
        path: 'team/' + encodeURIComponent(teamId) + '/join',
        data: {
            user_data_transfer_consent: true,
            additional_form_data: additionalFormData,
        },
        success: function (data) {
            if (!isApiSuccess(data)) {
                notifier.alert(getApiMessage(data, khMessage('channelCouldNotSubscribe', 'Channel could not be subscribed.')), {
                    durations: {
                        global: 5000
                    }
                });
                button.prop('disabled', false);
                channelSubscribeModalConfirm.prop('disabled', false);
                return;
            }

            const responseFields = getChannelAdditionalFieldsFromPayload(data);
            const responseTeamId = getChannelAdditionalTeamIdFromPayload(data) || teamId;
            const isWaitlist = isChannelWaitlistResponse(data);
            const redirectTeamId = !isWaitlist ? getGrantedChannelJoinTeamId(data, responseTeamId) : null;
            const additionalDataSaveFailed = getApiPayload(data)?.additional_form_data_saved === false;
            if (isWaitlist) {
                khStoreLeftChannelId(responseTeamId || teamId);
                khRemoveLeftChannelDom(responseTeamId || teamId);
            } else {
                khClearLeftChannelId(responseTeamId || teamId);
            }
            storeChannelSubscribeNotification(button, responseTeamId || teamId, data);
            if (!isWaitlist && responseTeamId && (additionalDataSaveFailed
                || (responseFields.length > 0 && pendingSubscribeAdditionalFields.length === 0))) {
                pendingSubscribeJoinedTeamId = responseTeamId;
                pendingSubscribeRedirectTeamId = redirectTeamId;
                pendingSubscribeTeamId = responseTeamId;
                if (pendingSubscribeAdditionalFields.length === 0) {
                    renderChannelSubscribeAdditionalFields(responseFields);
                }
                channelSubscribeModalConfirm
                    .prop('disabled', false)
                    .text(khMessage('channelSaveDataButton', 'Save channel data'));
                button.prop('disabled', false);
                notifier[additionalDataSaveFailed ? 'alert' : 'warning'](additionalDataSaveFailed
                    ? khMessage('channelDataCouldNotSave', 'Channel data could not be saved.')
                    : khMessage('channelAdditionalFormPrompt', 'Please complete the additional channel information.'), {
                    durations: {
                        global: 5000
                    }
                });
                return;
            }

            closeChannelSubscribeModal();

            if (isWaitlist) {
                notifier.warning(getChannelSubscribeSuccessMessage(data), {
                    durations: {
                        global: 5000
                    }
                });
            } else {
                notifier.success(getChannelSubscribeSuccessMessage(data), {
                    durations: {
                        global: 5000
                    }
                });
            }

            khFinishChannelJoin(redirectTeamId, 5000);
        },
        error: function () {
            notifier.alert(khMessage('channelCouldNotSubscribe', 'Channel could not be subscribed.'), {
                durations: {
                    global: 5000
                }
            });
            button.prop('disabled', false);
            channelSubscribeModalConfirm.prop('disabled', false);
        }
    });
}

jQuery(document).on("click", "button.subscribe-team", function () {
    const button = jQuery(this);
    const teamId = button.data('team-id');

    if (!teamId || button.prop('disabled')) {
        return;
    }

    openChannelSubscribeModal(button, teamId);
});

jQuery(document).on("click", ".channel-subscribe-close", function () {
    closeChannelSubscribeModal();
});

jQuery(document).on("click", "#channel-subscribe-modal", function (event) {
    if (event.target === this) {
        closeChannelSubscribeModal();
    }
});

jQuery(document).on("click", "#channel-subscribe-confirm", function () {
    if (!pendingSubscribeButton || !pendingSubscribeTeamId) {
        closeChannelSubscribeModal();
        return;
    }

    if (!channelSubscribeModalCheckbox.is(':checked')) {
        channelSubscribeModalCheckbox.addClass('is-invalid');
        setChannelSubscribeError(khMessage('channelSubscribeConsentError', 'Please confirm that your user information may be transferred to the channel.'));
        return;
    }

    channelSubscribeModalCheckbox.removeClass('is-invalid');
    setChannelSubscribeError('');

    const additionalFormResult = collectChannelSubscribeAdditionalFormData();
    if (!additionalFormResult.valid) {
        setChannelSubscribeError(additionalFormResult.message);
        return;
    }

    channelSubscribeModalConfirm.prop('disabled', true);

    submitChannelSubscription(pendingSubscribeButton, pendingSubscribeTeamId, additionalFormResult.data);
});

jQuery(document).on("click", "#join-team-confirm", function () {
    if (window.khChannelJoinByCodeHandledInTemplate) {
        return;
    }
    const codeJoinButton = jQuery(this);

    // Close any visible error
    jQuery("#join-team-error").hide();

    // Read correct input field from modal
    let code = jQuery('#team-access-code').val().trim();

    if (!code || code.length === 0) {
        jQuery("#join-team-error")
            .text(khMessage('channelAccessCodeRequired', 'Please enter an access code.'))
            .show();
        return;
    }

    let consentCheckbox = jQuery('#channel-code-user-data-consent');
    if (consentCheckbox.length > 0 && !consentCheckbox.is(':checked')) {
        consentCheckbox.addClass('is-invalid');
        jQuery("#join-team-error")
            .text(khMessage('channelSubscribeConsentError', 'Please confirm that your user information may be transferred to the channel.'))
            .show();
        return;
    }
    consentCheckbox.removeClass('is-invalid');

    let globalOptions = {
        labels: {
            success: khMessage('noticeChannel', 'Channel'),
            warning: khMessage('noticeChannel', 'Channel'),
            alert: khMessage('noticeChannel', 'Channel')
        }
    };
    let notifier = createKhNotifier(globalOptions);

    notifier.warning(khMessage('channelCheckingCode', 'Checking code...'), {

        durations: {
            global: 5000
        }
    });

    khRestAjax({
        method: 'POST',
        path: 'team/join-by-code',
        data: {
            code: code,
            user_data_transfer_consent: true,
        },
        success: function (data) {

            // Close modal
            jQuery("#join-team-modal").css("display", "none");

            let apiStatus = null;
            let payload = getApiPayload(data);

            if (payload && typeof payload.status !== "undefined") {
                apiStatus = String(payload.status).toLowerCase();
            }

            const isWaitlist = isChannelWaitlistResponse(data);

            if (apiStatus === "false") {
                notifier.alert(payload?.message || khMessage('error', 'Error'), {

                    durations: {global: 5000}
                });
            } else if (apiStatus === "true" || isWaitlist) {
                if (isWaitlist) {
                    const waitlistTeamId = getChannelAdditionalTeamIdFromPayload(data) || payload?.team_id || payload?.id || '';
                    if (waitlistTeamId) {
                        khStoreLeftChannelId(waitlistTeamId);
                        khRemoveLeftChannelDom(waitlistTeamId);
                    }
                    storeChannelSubscribeNotification(jQuery(), waitlistTeamId, data);
                    notifier.warning(channelWaitlistMessage, {
                        durations: {global: 5000}
                    });
                } else {
                    const responseTeamId = getChannelAdditionalTeamIdFromPayload(data);
                    const responseFields = getChannelAdditionalFieldsFromPayload(data);
                    if (responseTeamId && responseFields.length > 0) {
                        openChannelSubscribeModal(codeJoinButton, responseTeamId);
                        pendingSubscribeJoinedTeamId = responseTeamId;
                        pendingSubscribeRedirectTeamId = getGrantedChannelJoinTeamId(data);
                        renderChannelSubscribeAdditionalFields(responseFields);
                        channelSubscribeModalCheckbox.prop('checked', true);
                        channelSubscribeModalConfirm.text(khMessage('channelSaveDataButton', 'Save channel data'));
                        notifier.warning(khMessage('channelAdditionalFormPrompt', 'Please complete the additional channel information.'), {
                            durations: {global: 5000}
                        });
                        return;
                    }
                    notifier.success(payload?.message || khMessage('channelJoinSuccess', 'Channel successfully joined.'), {

                        durations: {global: 5000}
                    });
                }
            } else {
                notifier.alert(khMessage('unexpectedServerResponse', 'Unexpected server response.'), {

                    durations: {global: 5000}
                });
            }

            khFinishChannelJoin(!isWaitlist ? getGrantedChannelJoinTeamId(data) : null, 5000);
        },
        error: function () {
            jQuery("#join-team-error")
                .text(khMessage('channelAccessCodeInvalid', 'The access code is invalid or expired.'))
                .show();
        }
    });
});

let khChannelLeaveModalState = {
    button: null,
    onConfirm: null,
};

const khChannelLeaveEnsureModal = () => {
    let modal = document.getElementById('kh-channel-leave-modal');
    if (modal) {
        return modal;
    }

    modal = document.createElement('div');
    modal.id = 'kh-channel-leave-modal';
    modal.className = 'kh-modal kh-channel-leave-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
        <div class="kh-modal-card kh-channel-leave-card" role="dialog" aria-modal="true" aria-labelledby="kh-channel-leave-title">
            <div class="kh-modal-head">
                <h3 id="kh-channel-leave-title" class="kh-panel-title"></h3>
                <button type="button" class="kh-modal-close" data-channel-leave-cancel>${khMessage('close', 'Close')}</button>
            </div>
            <p class="kh-channel-leave-name" data-channel-leave-name></p>
            <p class="kh-channel-leave-text" data-channel-leave-text></p>
            <div class="kh-actions kh-channel-leave-actions">
                <button type="button" class="btn btn-secondary" data-channel-leave-cancel></button>
                <button type="button" class="btn btn-primary" data-channel-leave-confirm></button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
        if (event.target === modal || event.target.closest('[data-channel-leave-cancel]')) {
            khChannelLeaveCloseModal();
        }
    });

    modal.querySelector('[data-channel-leave-confirm]').addEventListener('click', () => {
        const onConfirm = khChannelLeaveModalState.onConfirm;
        khChannelLeaveCloseModal(false);

        if (typeof onConfirm === 'function') {
            onConfirm();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
            khChannelLeaveCloseModal();
        }
    });

    return modal;
};

const khChannelLeaveOpenModal = (button, teamName, onConfirm) => {
    const modal = khChannelLeaveEnsureModal();
    khChannelLeaveModalState.button = button;
    khChannelLeaveModalState.onConfirm = onConfirm;

    modal.querySelector('#kh-channel-leave-title').textContent = khMessage('channelLeaveModalTitle', 'Leave channel?');
    modal.querySelector('[data-channel-leave-name]').textContent = String(teamName || '').trim();
    modal.querySelector('[data-channel-leave-text]').textContent = khMessage(
        'channelLeaveModalText',
        'Do you really want to leave this channel? You will lose access to its content and provider-specific channel data.'
    );
    modal.querySelector('[data-channel-leave-cancel].btn').textContent = khMessage('channelLeaveCancel', 'Cancel');
    modal.querySelector('[data-channel-leave-confirm]').textContent = khMessage('channelLeaveConfirm', 'Leave channel');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    modal.querySelector('[data-channel-leave-cancel].btn').focus();
};

const khChannelLeaveCloseModal = (restoreFocus = true) => {
    const modal = document.getElementById('kh-channel-leave-modal');
    if (!modal) {
        return;
    }

    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    const button = khChannelLeaveModalState.button;
    khChannelLeaveModalState.button = null;
    khChannelLeaveModalState.onConfirm = null;

    if (restoreFocus && button && document.body.contains(button)) {
        button.focus();
    }
};

const submitChannelLeave = (button) => {
    let team_id = button.data('team-id');
    const teamName = String(button.data('team-name') || '').trim();
    let globalOptions = {
        labels: {
            info: khMessage('noticeChannel', 'Channel'),
            success: khMessage('noticeChannel', 'Channel'),
            warning: khMessage('noticeChannel', 'Channel'),
            alert: khMessage('noticeChannel', 'Channel')
        }
    };
    let notifier = createKhNotifier(globalOptions);

    if (!team_id || button.prop('disabled')) {
        return;
    }

    notifier.info(khMessage('channelUnsubscribing', 'Unsubscribing from channel...'), {

        durations: {
            global: 5000
        }
    });
    button.prop('disabled', true);

    khRestAjax({
        method: 'POST',
        path: 'team/' + encodeURIComponent(team_id) + '/leave',
        success: function (data) {
            if (!isChannelLeaveSuccessResponse(data)) {
                notifier.alert(getApiMessage(data, khMessage('channelCouldNotUnsubscribe', 'Channel could not be unsubscribed.')), {
                    durations: {
                        global: 5000
                    }
                });
                button.prop('disabled', false);
                return;
            }

            if (getApiMessage(data, '')) {
                notifier.success(getApiMessage(data, ''), {

                    durations: {
                        global: 5000
                    }
                });
            } else {
                notifier.success(khMessage('channelUnsubscribeSuccess', 'Channel successfully unsubscribed.'), {

                    durations: {
                        global: 5000
                    }
                });


            }

            khStoreLocalNotification({
                id: 'channel-left:' + String(team_id) + ':' + Date.now(),
                icon: 'groups',
                title: khMessage('channelLeaveNotificationTitle', 'Channel left'),
                message: khMessage('channelLeaveNotificationMessage', 'You have left the channel "%s".')
                    .replace('%s', teamName || khMessage('noticeChannel', 'Channel')),
                url: '',
            });

            TeejitRestClient.invalidateRuntimeCaches();
            khReloadWithRefreshData();
        },
        error: function (jqXHR) {
            notifier.alert(getApiMessage(jqXHR?.responseJSON, khMessage('channelCouldNotUnsubscribe', 'Channel could not be unsubscribed.')), {
                durations: {
                    global: 5000
                }
            });
            button.prop('disabled', false);
        }
    });
};

jQuery(document).on("click", "button.unsubscribe-team", function () {
    let button = jQuery(this);

    if (button.prop('disabled')) {
        return;
    }

    khChannelLeaveOpenModal(this, button.data('team-name'), () => submitChannelLeave(button));
});
jQuery(function ($) {

    const forbidden = /["'\\`]|^\s|\s$/;

    const errorTitle = passwordValidation.errorTitle;
    const errorMessage = passwordValidation.errorMessage;

    function attachPasswordValidation(formSelector, pwOldSelector, pw1Selector, pw2Selector, submitSelector) {

        const form = $(formSelector);
        const pwOld = pwOldSelector ? $(pwOldSelector) : null;
        const pw1 = $(pw1Selector);
        const pw2 = $(pw2Selector);
        const submitBtn = $(submitSelector);

        if (!form.length) return;
        if (!pw1.length || !pw2.length || !submitBtn.length) return;

        function showError(targetField) {
            $('#password-invalid-error').remove();

            const html =
                '<div id="password-invalid-error" class="notice notice-error">' +
                '<p><strong>' + errorTitle + ':</strong> ' + errorMessage + '</p>' +
                '</div>';

            // Registrierungsformular?
            let container = targetField.closest('.register-user-data');

            // Passwort ändern?
            if (!container.length) {
                container = targetField.closest('.t-table-cell');
            }

            // Falls gar nichts gefunden → Nachricht direkt hinter das Feld setzen
            if (!container.length) {
                container = targetField.parent();
            }

            container.append(html);

            submitBtn.prop('disabled', true).addClass('disabled-submit-btn');
        }

        function validateLive(field) {
            const val = field.val();
            $('#password-invalid-error').remove();

            if (forbidden.test(val)) {
                showError(field);
            } else {
                submitBtn.prop('disabled', false).removeClass('disabled-submit-btn');
            }
        }

        // On submit
        form.on('submit', function (e) {
            $('#password-invalid-error').remove();

            const val1 = pw1.val();
            const val2 = pw2.val();

            if (forbidden.test(val1) || forbidden.test(val2)) {
                e.preventDefault();
                showError(pw2);
                return false;
            }

            submitBtn.prop('disabled', false).removeClass('disabled-submit-btn');
            return true;
        });

        // Live validation
        pw1.on('input', () => validateLive(pw1));
        pw2.on('input', () => validateLive(pw2));
    }

    // Registration page
    attachPasswordValidation(
        '#t-registerform',
        null,
        '#user_password_one',
        '#user_password_two',
        '#teejit-register-submit'
    );

    // Change password page
    attachPasswordValidation(
        'form.edit-profil-data',
        '#password-0',
        '#password-1',
        '#password-2',
        '.btn.btn-primary'
    );

    // Forgot password page with manual password reset
    attachPasswordValidation(
        '#t-lostpassword-form',
        null,
        '#user_password_one',
        '#user_password_two',
        '#teejit-login-submit'
    );

});
